import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import AileSwitcher from "../components/AileSwitcher";
import AvatarImg from "../components/AvatarImg";
import AvatarPreviewModal from "../components/AvatarPreviewModal";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import AVATAR_IMPORTS from "../assets/avatar/index";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useLanguage } from "../i18n/LanguageContext";
import { translateKnownListValue } from "../utils/i18nOptions";
import {
  DEFAULT_NOTIFICATION_SETTINGS,
  getMyNotificationSettings,
  saveMyNotificationSettings,
  disableAllNotificationSettings,
  enableAllNotificationSettings,
} from "../services/notificationSettingsService";
import {
  getPushPermissionStatus,
  requestPushAccess,
  openPushSettings,
  setPushEnablePending,
  PUSH_PERMISSION_EVENT,
} from "../services/pushPermissionService";
import { requestAndSaveNotificationToken } from "../services/notificationService";
import { konumGecmisiniSilService } from "../services/haritaService";
import { getAssistantSummary, buildLocalAssistantFallback, readCachedAssistantSummary } from "../services/assistantService";
import {
  areAndroidHealthFeaturesDisabled,
  isAndroidRestrictedPage,
} from "../utils/platformFeatures";
import { profilFotografiniEditorIcinHazirla } from "../services/profilePictureService";
import useBodyScrollLock from "../hooks/useBodyScrollLock";
import { openStoreReviewPage } from "../services/appReviewService";
import { aileBildirimiGonder } from "../services/familyNotificationService";
import { aileDurumuGuncelle, aileDurumuKapat } from "../services/familyStatusService";
import { getRuntimeVersionLabel } from "../services/runtimeAppInfo";

import alisverisAsset from "../assets/alisverisasset.png";
import takvimAsset from "../assets/takvimasset.png";
import finansAsset from "../assets/finansasset.png";
import gorevlerAsset from "../assets/gorevlerasset.png";
import harcamalarAsset from "../assets/harcamalarasset.png";
import saglikAsset from "../assets/saglikasset.png";
import { appAlert, appConfirm } from "../components/AppDialog";
import FamilyInvitePanel from "../components/FamilyInvitePanel";
import PanelIcon from "../components/PanelIcon";
import aileHaritasiAsset from "../assets/aileharitasiasset.png";
import cocukTakibiAsset from "../assets/cocuktakibiasset.png";
import aileSohbetiAsset from "../assets/ailesohbetiasset.png";
import belgelerAsset from "../assets/belgelerasset.png";
import nePisirsemAsset from "../assets/nepisirsemasset.png";
import ailemAsset from "../assets/ailemasset.png";
import yonetimAsset from "../assets/yonetimasset.png";
import aileAsistaniAsset from "../assets/aileasistaniasset.png";


const ROL_SIRALAMASI = ["Kişisel", "Baba", "Anne", "Çocuk", "Amca", "Hala", "Teyze", "Dayı", "Dede", "Nene", "Diğer"];
const normalizeFamilyProfileRole = (role) => {
  const value = String(role || "").trim();
  // Eski hatalı kayıt/çeviri eşleşmelerinden gelen çocuk rolü düzeltmesi.
  // Bu normalizasyon yalnızca aile rolü gösteriminde kullanılır; gider/ulaşım kategorilerine dokunmaz.
  if (["Toplu Taşıma", "Public Transport", "Children"].includes(value)) return "Çocuk";
  return value;
};

const ABOUT_LINKS = Object.freeze({
  instagram: "https://www.instagram.com/aileyonetimiapp/",
  website: "https://aileyonetimi.com",
});

const openAboutLink = async (url) => {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url, presentationStyle: "popover" });
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

const formatMemberPresenceText = (member, lang = "tr", currentUid = "") => {
  const memberUid = String(member?.uid || member?.id || "").trim();
  if (memberUid && currentUid && memberUid === String(currentUid).trim()) return lang === "en" ? "You" : "Siz";
  if (member?.isOnline || member?.online) return lang === "en" ? "Online now" : "Şu an aktif";
  const raw = member?.lastActiveAt || member?.lastSeenAt;
  if (!raw) return lang === "en" ? "Not active" : "Aktif değil";
  const time = new Date(raw).getTime();
  if (!time || Number.isNaN(time)) return lang === "en" ? "Not active" : "Aktif değil";
  const diffMin = Math.max(0, Math.floor((Date.now() - time) / 60000));
  if (diffMin < 1) return lang === "en" ? "Just now" : "Az önce";
  if (diffMin < 60) return lang === "en" ? `${diffMin}m ago` : `${diffMin} dk önce`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return lang === "en" ? `${diffHour}h ago` : `${diffHour} sa önce`;
  const diffDay = Math.floor(diffHour / 24);
  return lang === "en" ? `${diffDay}d ago` : `${diffDay} gün önce`;
};

const FAMILY_STATUS_OPTIONS = [
  { id: "home", emoji: "🏠", tr: "Evdeyim", en: "At home", tone: "green" },
  { id: "road", emoji: "🚗", tr: "Yoldayım", en: "On my way", tone: "blue" },
  { id: "work", emoji: "💼", tr: "İşteyim", en: "At work", tone: "violet" },
  { id: "school", emoji: "🎓", tr: "Okuldayım", en: "At school", tone: "cyan" },
  { id: "busy", emoji: "🔕", tr: "Müsait değilim", en: "Unavailable", tone: "slate" },
  { id: "safe", emoji: "✓", tr: "Güvendeyim", en: "I'm safe", tone: "emerald" },
];

const familyStatusVisibilityStorageKey = (uid, familyId) => `fp:family-status-visible:${uid || "guest"}:${familyId || "none"}`;
const readFamilyStatusVisibility = (uid, familyId) => {
  try { return localStorage.getItem(familyStatusVisibilityStorageKey(uid, familyId)) !== "false"; }
  catch { return true; }
};

const inferFamilyStatusDuration = (record) => {
  if (!record || typeof record !== "object") return null;
  if (["30m", "1h", "2h", "manual"].includes(record.duration)) return record.duration;
  if (Object.prototype.hasOwnProperty.call(record, "expiresAt") && !record.expiresAt) return "manual";
  const updatedAt = new Date(record.updatedAt || record.updated_at || "").getTime();
  const expiresAt = new Date(record.expiresAt || record.expires_at || "").getTime();
  if (!Number.isFinite(updatedAt) || !Number.isFinite(expiresAt) || expiresAt <= updatedAt) return "1h";
  const difference = expiresAt - updatedAt;
  if (Math.abs(difference - (30 * 60 * 1000)) <= 60000) return "30m";
  if (Math.abs(difference - (2 * 60 * 60 * 1000)) <= 60000) return "2h";
  return "1h";
};

function FamilyStatusPreview({ profil, user, familyId, uyeler = [], lang = "tr", accentColor = "#6366f1" }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeStatus, setActiveStatus] = useState(null);
  const [activeStatusUpdatedAt, setActiveStatusUpdatedAt] = useState(null);
  const [activeStatusExpiresAt, setActiveStatusExpiresAt] = useState(null);
  const [activeStatusDuration, setActiveStatusDuration] = useState(null);
  const [statusClock, setStatusClock] = useState(() => Date.now());
  const [draftStatus, setDraftStatus] = useState("home");
  const [duration, setDuration] = useState("1h");
  const [notifyFamily, setNotifyFamily] = useState(true);
  const [statusSaveState, setStatusSaveState] = useState("idle");
  const [selectedMember, setSelectedMember] = useState(null);
  const [requestState, setRequestState] = useState("idle");
  const requestResetTimerRef = useRef(null);
  useBodyScrollLock(panelOpen || Boolean(selectedMember));

  const currentUid = String(user?.firebase_uid || user?.uid || profil?.firebase_uid || profil?.uid || "").trim();
  const currentMember = uyeler.find((member) => String(member?.uid || member?.id || "").trim() === currentUid) || profil || {};
  const persistedCurrentStatus = currentMember?.familyStatus || currentMember?.family_status || currentMember?.familyStatusInfo || null;
  const persistedCurrentStatusKey = typeof persistedCurrentStatus === "string"
    ? persistedCurrentStatus
    : (persistedCurrentStatus?.key || persistedCurrentStatus?.statusKey || persistedCurrentStatus?.status_key || "");
  const persistedCurrentStatusUpdatedAt = typeof persistedCurrentStatus === "object" && persistedCurrentStatus
    ? (persistedCurrentStatus.updatedAt || persistedCurrentStatus.updated_at || null)
    : null;
  const persistedCurrentStatusExpiresAt = typeof persistedCurrentStatus === "object" && persistedCurrentStatus
    ? (persistedCurrentStatus.expiresAt || persistedCurrentStatus.expires_at || null)
    : null;
  const otherMembers = uyeler
    .filter((member) => String(member?.uid || member?.id || "").trim() !== currentUid);
  const visibleMembers = [currentMember, ...otherMembers].filter(Boolean);
  const selectedOption = FAMILY_STATUS_OPTIONS.find((option) => option.id === activeStatus);
  const draftOption = FAMILY_STATUS_OPTIONS.find((option) => option.id === draftStatus) || FAMILY_STATUS_OPTIONS[0];
  const durationLabels = {
    "30m": lang === "en" ? "30 min" : "30 dk",
    "1h": lang === "en" ? "1 hour" : "1 saat",
    "2h": lang === "en" ? "2 hours" : "2 saat",
    manual: lang === "en" ? "Until I turn it off" : "Ben kapatana kadar",
  };

  const memberName = (member, isCurrent = false) => {
    if (isCurrent) return lang === "en" ? "You" : "Siz";
    return `${member?.ad || ""} ${member?.soyad || ""}`.trim() || member?.displayName || member?.email || (lang === "en" ? "Family member" : "Aile üyesi");
  };
  const memberInitials = (member) => `${member?.ad?.[0] || ""}${member?.soyad?.[0] || ""}`.trim() || member?.displayName?.[0] || "?";
  const openPanel = () => {
    setDraftStatus(activeStatus || "home");
    setDuration(activeStatusDuration || "1h");
    setStatusSaveState("idle");
    setPanelOpen(true);
  };
  useEffect(() => {
    const expiry = persistedCurrentStatusExpiresAt ? new Date(persistedCurrentStatusExpiresAt).getTime() : null;
    const expired = Number.isFinite(expiry) && expiry <= Date.now();
    setActiveStatus(expired ? null : (persistedCurrentStatusKey || null));
    setActiveStatusUpdatedAt(expired ? null : persistedCurrentStatusUpdatedAt);
    setActiveStatusExpiresAt(expired ? null : persistedCurrentStatusExpiresAt);
    setActiveStatusDuration(expired ? null : inferFamilyStatusDuration(persistedCurrentStatus));
  }, [familyId, currentUid, persistedCurrentStatus, persistedCurrentStatusKey, persistedCurrentStatusUpdatedAt, persistedCurrentStatusExpiresAt]);
  useEffect(() => {
    if (!activeStatusExpiresAt) return undefined;
    const expiresAt = new Date(activeStatusExpiresAt).getTime();
    if (!Number.isFinite(expiresAt)) return undefined;
    const clearExpiredStatus = () => {
      setActiveStatus(null);
      setActiveStatusUpdatedAt(null);
      setActiveStatusExpiresAt(null);
      setActiveStatusDuration(null);
      setStatusClock(Date.now());
    };
    if (expiresAt <= Date.now()) {
      clearExpiredStatus();
      return undefined;
    }
    const timer = setTimeout(clearExpiredStatus, Math.min(expiresAt - Date.now() + 100, 2147483647));
    return () => clearTimeout(timer);
  }, [activeStatusExpiresAt]);
  useEffect(() => {
    const expiries = otherMembers
      .map((member) => member?.familyStatus || member?.family_status || member?.familyStatusInfo || null)
      .map((status) => new Date(status?.expiresAt || status?.expires_at || "").getTime())
      .filter((expiry) => Number.isFinite(expiry) && expiry > Date.now());
    if (!expiries.length) return undefined;
    const nearestExpiry = Math.min(...expiries);
    const timer = setTimeout(() => setStatusClock(Date.now()), Math.min(nearestExpiry - Date.now() + 100, 2147483647));
    return () => clearTimeout(timer);
  }, [otherMembers]);
  useEffect(() => {
    const handleOpenFamilyStatus = () => openPanel();
    window.addEventListener("fp:openFamilyStatus", handleOpenFamilyStatus);
    return () => window.removeEventListener("fp:openFamilyStatus", handleOpenFamilyStatus);
  });
  const applyStatus = async () => {
    if (statusSaveState === "saving") return;
    const sameActiveSelection = activeStatus === draftStatus && activeStatusDuration === duration;
    if (sameActiveSelection) return;
    try {
      setStatusSaveState("saving");
      const result = await aileDurumuGuncelle({ familyId, userUid: currentUid, statusKey: draftStatus, duration });
      const saved = result.familyStatus;
      setActiveStatus(saved.key);
      setActiveStatusUpdatedAt(saved.updatedAt);
      setActiveStatusExpiresAt(saved.expiresAt || null);
      setActiveStatusDuration(duration);
      if (notifyFamily && result.shouldNotify) {
        const targetUids = uyeler
          .map((member) => String(member?.uid || member?.id || "").trim())
          .filter((uid) => uid && uid !== currentUid);
        if (targetUids.length > 0) {
          const senderName = `${profil?.ad || ""} ${profil?.soyad || ""}`.trim() || (lang === "en" ? "A family member" : "Bir aile üyen");
          await aileBildirimiGonder({
            familyId,
            uyeler,
            hedefUidler: targetUids,
            baslik: lang === "en" ? `${draftOption.emoji} Family status updated` : `${draftOption.emoji} Aile durumu güncellendi`,
            aciklama: lang === "en" ? `${senderName}: ${draftOption.en}` : `${senderName}: ${draftOption.tr}`,
            gonderenUid: currentUid,
            gonderimTuru: "normal",
            bildirimType: "familyStatusUpdated",
            kaynak: "familyStatus",
            url: "/?pushPage=ana&action=familyStatus",
            idempotencyKey: `familyStatusUpdated_${familyId}_${currentUid}_${saved.notificationRevision || saved.updatedAt}`,
            respectQuietHours: true,
            statusKey: saved.key,
            statusUpdatedAt: saved.updatedAt,
            statusRevision: saved.notificationRevision || null,
          }).catch(() => {});
        }
      }
      setPanelOpen(false);
      setStatusSaveState("idle");
    } catch {
      setStatusSaveState("error");
    }
  };

  const clearStatus = async () => {
    if (!activeStatus || statusSaveState === "saving" || statusSaveState === "clearing") return;
    try {
      setStatusSaveState("clearing");
      await aileDurumuKapat({ familyId, userUid: currentUid });
      setActiveStatus(null);
      setActiveStatusUpdatedAt(null);
      setActiveStatusExpiresAt(null);
      setActiveStatusDuration(null);
      setPanelOpen(false);
      setStatusSaveState("idle");
    } catch {
      setStatusSaveState("error");
    }
  };

  useEffect(() => () => clearTimeout(requestResetTimerRef.current), []);

  const readMemberStatus = (member, isCurrent = false) => {
    if (isCurrent && selectedOption) return { option: selectedOption, updatedAt: activeStatusUpdatedAt };
    const raw = member?.familyStatus || member?.family_status || member?.familyStatusInfo || null;
    const key = typeof raw === "string"
      ? raw
      : (raw?.key || raw?.statusKey || raw?.status_key || member?.familyStatusKey || member?.family_status_key || "");
    const option = FAMILY_STATUS_OPTIONS.find((item) => item.id === key) || null;
    const updatedAt = typeof raw === "object" && raw
      ? (raw.updatedAt || raw.updated_at || raw.createdAt || null)
      : (member?.familyStatusUpdatedAt || member?.family_status_updated_at || null);
    const expiresAt = typeof raw === "object" && raw
      ? (raw.expiresAt || raw.expires_at || null)
      : (member?.familyStatusExpiresAt || member?.family_status_expires_at || null);
    if (expiresAt) {
      const expiry = new Date(expiresAt).getTime();
      if (Number.isFinite(expiry) && expiry <= statusClock) return { option: null, updatedAt: null };
    }
    return { option, updatedAt };
  };

  const formatStatusTime = (value) => {
    if (!value) return lang === "en" ? "No update time" : "Güncelleme zamanı yok";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return lang === "en" ? "No update time" : "Güncelleme zamanı yok";
    return date.toLocaleString(lang === "en" ? "en-US" : "tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const sameActiveStatus = Boolean(activeStatus && activeStatus === draftStatus);
  const sameActiveSelection = sameActiveStatus && activeStatusDuration === duration;
  const durationOnlyChange = sameActiveStatus && !sameActiveSelection;

  const requestStatusUpdate = async () => {
    const targetUid = String(selectedMember?.uid || selectedMember?.id || "").trim();
    if (!targetUid || !currentUid || requestState === "sending" || requestState === "sent") return;
    const senderName = `${profil?.ad || ""} ${profil?.soyad || ""}`.trim() || (lang === "en" ? "A family member" : "Bir aile üyen");
    try {
      setRequestState("sending");
      await aileBildirimiGonder({
        familyId,
        uyeler,
        hedefUidler: [targetUid],
        baslik: lang === "en" ? "📍 Please update your status" : "📍 Durumunu günceller misin?",
        aciklama: lang === "en" ? `${senderName} asked you to update your family status.` : `${senderName}, aile durumunu güncellemeni istiyor.`,
        gonderenUid: currentUid,
        gonderimTuru: "normal",
        bildirimType: "familyStatusUpdateRequest",
        kaynak: "familyStatus",
        url: "/?pushPage=ana&action=familyStatus",
        idempotencyKey: `familyStatusRequest_${familyId}_${currentUid}_${targetUid}_${Math.floor(Date.now() / 900000)}`,
        respectQuietHours: true,
        cooldownSeconds: 900,
      });
      setRequestState("sent");
      clearTimeout(requestResetTimerRef.current);
      requestResetTimerRef.current = setTimeout(() => setRequestState("idle"), 60000);
    } catch {
      setRequestState("error");
      clearTimeout(requestResetTimerRef.current);
      requestResetTimerRef.current = setTimeout(() => setRequestState("idle"), 4000);
    }
  };

  if (visibleMembers.length < 2) return null;

  return (
    <>
      <section className="familyStatusCard" data-tour-id="family-status" style={{ "--family-status-accent": accentColor }} aria-label={lang === "en" ? "Family status preview" : "Aile durumu önizlemesi"}>
        <div className={`familyStatusMembers${visibleMembers.length === 2 ? " twoMembers" : ""}`}>
          {visibleMembers.map((member, index) => {
            const isCurrent = index === 0;
            const memberColor = member?.calendarColor || member?.color || member?.renk || (isCurrent ? accentColor : "#64748b");
            const memberStatus = readMemberStatus(member, isCurrent);
            return (
              <button type="button" data-family-status-member className={`familyStatusMember${isCurrent ? " isCurrent" : ""}`} key={member?.uid || member?.id || `status-member-${index}`} onClick={() => { if (isCurrent) openPanel(); else { setSelectedMember(member); setRequestState("idle"); } }}>
                <span className="familyStatusAvatarShell">
                  <AvatarImg avatar={member?.avatar || null} renk={memberColor} initials={memberInitials(member)} size={32} glow={Boolean(memberStatus.option)} uid={member?.uid || member?.id} isOnline={member?.isOnline === true || member?.online === true} />
                  {memberStatus.option && <span className={`familyStatusMiniIcon ${memberStatus.option.tone}`} aria-hidden="true">{memberStatus.option.emoji}</span>}
                </span>
                <span className="familyStatusMemberText">
                  <strong>{memberName(member, isCurrent)}</strong>
                  <small className={memberStatus.option ? "hasStatus" : ""}>
                    {memberStatus.option
                      ? `${memberStatus.option[lang === "en" ? "en" : "tr"]}${isCurrent && activeStatusDuration && activeStatusDuration !== "manual" ? ` · ${durationLabels[activeStatusDuration]}` : ""}`
                      : (lang === "en" ? "No Status" : "Durum Yok")}
                  </small>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {panelOpen && (
        <div className="familyStatusSheetBackdrop" onClick={() => setPanelOpen(false)}>
          <div className="familyStatusSheet" role="dialog" aria-modal="true" aria-labelledby="family-status-sheet-title" onClick={(event) => event.stopPropagation()}>
            <div className="familyStatusSheetHandle" aria-hidden="true" />
            <div className="familyStatusSheetHead">
              <div><span>{lang === "en" ? "ONE TAP UPDATE" : "TEK DOKUNUŞLA HABER VER"}</span><h2 id="family-status-sheet-title">{lang === "en" ? "Share your status" : "Durumunu paylaş"}</h2><p>{lang === "en" ? "Let your family know without writing a message." : "Mesaj yazmadan ailene haber ver."}</p></div>
              <button type="button" onClick={() => setPanelOpen(false)} aria-label={lang === "en" ? "Close" : "Kapat"}>×</button>
            </div>
            <div className="familyStatusOptionGrid">
              {FAMILY_STATUS_OPTIONS.map((option) => (
                <button key={option.id} type="button" className={`${option.tone}${draftStatus === option.id ? " selected" : ""}`} onClick={() => setDraftStatus(option.id)} aria-pressed={draftStatus === option.id}>
                  <span aria-hidden="true">{option.emoji}</span><strong>{option[lang === "en" ? "en" : "tr"]}</strong>{draftStatus === option.id && <i aria-hidden="true">✓</i>}
                </button>
              ))}
            </div>
            <div className="familyStatusDurationBlock">
              <div className="familyStatusBlockTitle"><span>{lang === "en" ? "Duration" : "Ne kadar görünsün?"}</span><small>{draftOption.emoji} {draftOption[lang === "en" ? "en" : "tr"]}</small></div>
              <div className="familyStatusDurationOptions">
                {Object.entries(durationLabels).map(([value, label]) => <button key={value} type="button" className={duration === value ? "selected" : ""} onClick={() => setDuration(value)}>{label}</button>)}
              </div>
            </div>
            <button type="button" className={`familyStatusNotifyToggle${notifyFamily ? " active" : ""}${sameActiveStatus ? " disabled" : ""}`} onClick={() => setNotifyFamily((current) => !current)} aria-pressed={notifyFamily} disabled={sameActiveStatus}>
              <span className="familyStatusNotifyIcon" aria-hidden="true">🔔</span><span><strong>{lang === "en" ? "Notify my family" : "Aileme bildirim gönder"}</strong><small>{sameActiveStatus ? (lang === "en" ? "The same status never sends another notification." : "Aynı durum için tekrar bildirim gönderilmez.") : (lang === "en" ? "A single quiet notification will be sent." : "Yalnızca bir sessiz bildirim gönderilir.")}</small></span><i aria-hidden="true"><b /></i>
            </button>
            <button type="button" className={`familyStatusApplyBtn${statusSaveState === "error" ? " error" : ""}${sameActiveSelection ? " unchanged" : ""}`} onClick={applyStatus} disabled={statusSaveState === "saving" || statusSaveState === "clearing" || sameActiveSelection}>
              <span aria-hidden="true">{statusSaveState === "error" ? "!" : draftOption.emoji}</span>
              {statusSaveState === "saving"
                ? (durationOnlyChange ? (lang === "en" ? "Updating duration…" : "Süre güncelleniyor…") : (lang === "en" ? "Sharing…" : "Paylaşılıyor…"))
                : statusSaveState === "error"
                  ? (lang === "en" ? "Could not save · Try again" : "Kaydedilemedi · Tekrar dene")
                  : sameActiveSelection
                    ? (lang === "en" ? "This status is already active" : "Bu durum zaten aktif")
                    : durationOnlyChange
                      ? (lang === "en" ? "Update duration" : "Süreyi Güncelle")
                      : (lang === "en" ? "Share status" : "Durumu Paylaş")}
            </button>
            {activeStatus && (
              <button type="button" className="familyStatusClearBtn" onClick={clearStatus} disabled={statusSaveState === "saving" || statusSaveState === "clearing"}>
                <span aria-hidden="true">{statusSaveState === "clearing" ? "…" : "×"}</span>
                {statusSaveState === "clearing"
                  ? (lang === "en" ? "Turning off…" : "Kapatılıyor…")
                  : (lang === "en" ? "Turn off my status" : "Durumumu Kapat")}
              </button>
            )}
            <p className="familyStatusPrototypeNote">{lang === "en" ? "Your status is visible only to members of this family." : "Durumun yalnızca bu ailedeki üyeler tarafından görülür."}</p>
          </div>
        </div>
      )}

      {selectedMember && (() => {
        const status = readMemberStatus(selectedMember, false);
        const color = selectedMember?.calendarColor || selectedMember?.color || selectedMember?.renk || "#64748b";
        return (
          <div className="familyStatusSheetBackdrop familyStatusMemberBackdrop" onClick={() => setSelectedMember(null)}>
            <div className="familyStatusMemberSheet" role="dialog" aria-modal="true" aria-labelledby="family-member-status-title" onClick={(event) => event.stopPropagation()}>
              <div className="familyStatusSheetHandle" aria-hidden="true" />
              <button type="button" className="familyStatusMemberClose" onClick={() => setSelectedMember(null)} aria-label={lang === "en" ? "Close" : "Kapat"}>×</button>
              <div className="familyStatusMemberHero">
                <span className="familyStatusMemberLargeAvatar">
                  <AvatarImg avatar={selectedMember?.avatar || null} renk={color} initials={memberInitials(selectedMember)} size={64} glow={Boolean(status.option)} uid={selectedMember?.uid || selectedMember?.id} isOnline={selectedMember?.isOnline === true || selectedMember?.online === true} />
                  {status.option && <span className={`familyStatusMiniIcon ${status.option.tone}`} aria-hidden="true">{status.option.emoji}</span>}
                </span>
                <span className="familyStatusMemberSheetEyebrow">{lang === "en" ? "FAMILY STATUS" : "AİLE DURUMU"}</span>
                <h2 id="family-member-status-title">{memberName(selectedMember)}</h2>
              </div>
              <div className={`familyStatusCurrentState${status.option ? " hasStatus" : ""}`}>
                <span className="familyStatusCurrentIcon" aria-hidden="true">{status.option?.emoji || "—"}</span>
                <span><small>{lang === "en" ? "Current status" : "Mevcut durum"}</small><strong>{status.option ? status.option[lang === "en" ? "en" : "tr"] : (lang === "en" ? "No Status" : "Durum Yok")}</strong></span>
                <time>{formatStatusTime(status.updatedAt)}</time>
              </div>
              <button type="button" className={`familyStatusRequestBtn ${requestState}`} onClick={requestStatusUpdate} disabled={requestState === "sending" || requestState === "sent"}>
                <span aria-hidden="true">{requestState === "sent" ? "✓" : requestState === "error" ? "!" : "🔔"}</span>
                {requestState === "sending"
                  ? (lang === "en" ? "Sending…" : "Gönderiliyor…")
                  : requestState === "sent"
                    ? (lang === "en" ? "Request sent" : "İstek gönderildi")
                    : requestState === "error"
                      ? (lang === "en" ? "Could not send · Try again" : "Gönderilemedi · Tekrar dene")
                      : (lang === "en" ? "Request a status update" : "Durum güncellemesi iste")}
              </button>
              <p className="familyStatusRequestNote">{lang === "en" ? "Only this family member will receive a notification." : "Bildirim yalnızca bu aile üyesine gönderilir."}</p>
            </div>
          </div>
        );
      })()}
    </>
  );
}
const uyelerSirala = (liste) =>
  [...liste].sort((a,b) => {
    const ai = ROL_SIRALAMASI.indexOf(normalizeFamilyProfileRole(a.rol || a.profilRol));
    const bi = ROL_SIRALAMASI.indexOf(normalizeFamilyProfileRole(b.rol || b.profilRol));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

/* ─────────────────────────────────────────
   PROFİL PANELİ
───────────────────────────────────────── */
function ProfilPanel({ profil, cikisYap, profilGuncelle, profilFotografiYukle, profilFotografiSil, sifreGuncelle, kapat }) {
  const { t, tList, lang } = useLanguage();
  const [sekme, setSekme] = useState("bilgi");
  const [yeniAd, setYeniAd] = useState(profil?.ad || "");
  const [yeniSoyad, setYeniSoyad] = useState(profil?.soyad || "");
  const [yeniTelefon, setYeniTelefon] = useState(profil?.telefon || "");
  const [yeniDogum, setYeniDogum] = useState(profil?.dogumTarihi || "");
  const [mevcutSifre, setMevcutSifre] = useState("");
  const [yeniSifre, setYeniSifre] = useState("");
  const [yeniSifreTekrar, setYeniSifreTekrar] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [hata, setHata] = useState("");
  const [yukleniyor, setYukleniyor] = useState(false);
  const [seciliAvatar, setSeciliAvatar] = useState(profil?.avatar || null);
  const [seciliRenk, setSeciliRenk] = useState(profil?.calendarColor || "#6366f1");
  const [avatarYukleniyor, setAvatarYukleniyor] = useState(false);
  const [fotoYuklemeYuzdesi, setFotoYuklemeYuzdesi] = useState(0);
  const [fotoEditorDosyasi, setFotoEditorDosyasi] = useState(null);
  const [fotoHazirlaniyor, setFotoHazirlaniyor] = useState(false);
  const [avatarPreviewOpen, setAvatarPreviewOpen] = useState(false);
  const sifreIslemiRef = useRef(false);
  const kameraInputRef = useRef(null);
  const galeriInputRef = useRef(null);
  const initials = `${profil?.ad?.[0]||""}${profil?.soyad?.[0]||""}`.toUpperCase();
  const kullaniciRenk = seciliRenk || profil?.calendarColor || "#6366f1";
  const hasPassword = profil?.hasPassword !== false;
  const storedProfilePhoto = profil?.profilePictureUrl
    || (profil?.profilePictureFileKey && profil?.avatarKind === "custom" && profil?.avatar ? profil.avatar : null);
  const builtInAvatarMi = AVATAR_LIST.some(({ file }) => file === seciliAvatar);

  useEffect(() => {
    setSeciliAvatar(profil?.avatar || null);
    setSeciliRenk(profil?.calendarColor || "#6366f1");
  }, [profil?.avatar, profil?.calendarColor]);

  const profilKaydet = async () => {
    setHata(""); setMesaj("");
    if (!yeniAd.trim() || !yeniSoyad.trim()) { setHata(t("profile.nameRequired")); return; }
    try { setYukleniyor(true); await profilGuncelle({ yeniAd, yeniSoyad, yeniTelefon, yeniDogumTarihi: yeniDogum }); setMesaj(t("profile.updatedOk")); setTimeout(()=>{setMesaj("");setSekme("bilgi");},1500); }
    catch { setHata(t("profile.updateFailed")); } finally { setYukleniyor(false); }
  };

  const sifreKaydet = async () => {
    if (sifreIslemiRef.current) return;
    setHata(""); setMesaj("");
    if ((hasPassword && !mevcutSifre) || !yeniSifre) { setHata(t("profile.fillAll")); return; }
    if (yeniSifre.length < 8 || yeniSifre.length > 128) { setHata(t("profile.passwordMinChars")); return; }
    if (yeniSifre!==yeniSifreTekrar) { setHata(t("profile.passwordMismatch")); return; }
    try { sifreIslemiRef.current = true; setYukleniyor(true); await sifreGuncelle({mevcutSifre,yeniSifre}); setMesaj(t(hasPassword ? "profile.passwordUpdatedOk" : "profile.passwordCreatedOk")); setMevcutSifre(""); setYeniSifre(""); setYeniSifreTekrar(""); setTimeout(()=>setMesaj(""),2000); }
    catch(e) { setHata(e.code==="invalid_current_password"?t("profile.currentPasswordWrong"):t("profile.passwordUpdateFailed")); } finally { sifreIslemiRef.current = false; setYukleniyor(false); }
  };

  const renkSec = async (renk) => {
    setSeciliRenk(renk);
    setHata("");
    try {
      await profilGuncelle({ yeniCalendarColor: renk });
    } catch {
      setHata(t("profile.avatar.saveFail"));
    }
  };

  const avatarSec = async (avatar) => {
    if (avatarYukleniyor || avatar === seciliAvatar) return;
    setHata("");
    setMesaj("");
    try {
      setAvatarYukleniyor(true);
      await profilGuncelle({ yeniAvatar: avatar, yeniCalendarColor: seciliRenk });
      setSeciliAvatar(avatar || null);
    } catch {
      setHata(t("profile.avatar.saveFail"));
    } finally {
      setAvatarYukleniyor(false);
    }
  };

  const fotoDosyasiSecildi = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const allowedByType = String(file.type || "").startsWith("image/");
    const allowedByName = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || "");
    if (!allowedByType && !allowedByName) {
      setHata(t("profile.photo.unsupported"));
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setHata(t("profile.photo.tooLarge"));
      return;
    }
    setHata("");
    setMesaj("");
    try {
      setFotoHazirlaniyor(true);
      const preparedFile = await profilFotografiniEditorIcinHazirla(file);
      setFotoEditorDosyasi(preparedFile);
    } catch (error) {
      console.warn("[ProfilPanel] Fotoğraf editör için hazırlanamadı:", error?.code || error?.message);
      setHata(t("profile.photo.editorError"));
    } finally {
      setFotoHazirlaniyor(false);
    }
  };

  const kirpilmisFotografiKaydet = async (blob) => {
    setHata("");
    setMesaj("");
    setFotoYuklemeYuzdesi(0);
    try {
      setAvatarYukleniyor(true);
      const result = await profilFotografiYukle(blob, setFotoYuklemeYuzdesi);
      setSeciliAvatar(result?.avatar || null);
      setFotoEditorDosyasi(null);
    } catch (error) {
      console.warn("[ProfilPanel] Profil fotoğrafı yüklenemedi:", error?.code || error?.message);
      setHata(error?.code === "PROFILE_PHOTO_TOO_LARGE" ? t("profile.photo.tooLarge") : t("profile.photo.uploadFailed"));
    } finally {
      setAvatarYukleniyor(false);
      setFotoYuklemeYuzdesi(0);
    }
  };

  const fotografiKaldir = async () => {
    const onay = await appConfirm(t("profile.photo.removeConfirm"), {
      title: t("profile.photo.removeTitle"),
      confirmText: t("profile.photo.remove"),
      cancelText: t("common.cancel"),
      danger: true,
    });
    if (!onay) return;
    setHata("");
    setMesaj("");
    try {
      setAvatarYukleniyor(true);
      const result = await profilFotografiSil(builtInAvatarMi ? seciliAvatar : null);
      setSeciliAvatar(result?.avatar || null);
    } catch {
      setHata(t("profile.avatar.saveFail"));
    } finally {
      setAvatarYukleniyor(false);
    }
  };

  const cikisYapOnayli = async () => {
    const onay = await appConfirm(
      lang === "en"
        ? "Do you want to sign out? You will need to sign in again to continue."
        : "Çıkış yapmak istediğine emin misin? Devam etmek için tekrar giriş yapman gerekecek.",
      {
        title: lang === "en" ? "Sign out?" : "Çıkış yapılsın mı?",
        confirmText: t("profile.logout"),
        cancelText: t("common.cancel"),
        danger: true,
      }
    );
    if (!onay) return;
    cikisYap();
  };

  /* Rolüne göre önerilen avatarlar üste, geri kalanlar alta */
  const userRol = profil?.rol || "";
  const onerilens = AVATAR_LIST.filter(a => a.roles.includes(userRol));
  const digerler  = AVATAR_LIST.filter(a => !a.roles.includes(userRol));
  const currentAvatar = seciliAvatar;

  return (
    <div className="profilPanelOverlay" onClick={kapat}>
      <div className="profilPanel" onClick={e=>e.stopPropagation()}>
        <div className="profilPanelTop">
          <div
            className="profilBigAvatarWrap"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (sekme === "avatar") {
                setAvatarPreviewOpen(true);
              } else {
                setSekme("avatar");
                setHata("");
                setMesaj("");
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (sekme === "avatar") {
                  setAvatarPreviewOpen(true);
                } else {
                  setSekme("avatar");
                  setHata("");
                  setMesaj("");
                }
              }
            }}
            aria-label={lang === "en" ? "View profile avatar" : "Profil avatarını görüntüle"}
            title={lang === "en" ? "View avatar" : "Avatarı görüntüle"}
          >
            <AvatarImg
              avatar={currentAvatar}
              uid={profil?.firebase_uid || profil?.uid || profil?.id}
              fileKey={profil?.profilePictureFileKey}
              renk={kullaniciRenk}
              initials={initials || "FP"}
              size={72}
              glow={true}
              radius="50%"
            />
            <div className="profilBigAvatarEdit" aria-hidden="true">⌕</div>
          </div>
          <div className="profilPanelName">{profil?.ad} {profil?.soyad}</div>
          <div className="profilPanelRole">{translateKnownListValue(tList, profil?.rol) || profil?.rol} · {translateKnownListValue(tList, profil?.aileYetkisi) || t("family.member")}</div>
        </div>
        <div className="profilTabs">
          {[["bilgi", t("profile.tab.info"), "info"],["duzenle", t("profile.tab.edit"), "edit"],["sifre", t("profile.tab.password"), "password"],["avatar", t("profile.tab.avatar"), "avatar"]].map(([k,l,icon])=>(
            <button key={k} className={sekme===k?"profilTab active":"profilTab"} onClick={()=>{setSekme(k);setHata("");setMesaj("");}}><PanelIcon type={icon} />{l}</button>
          ))}
        </div>
        <div className="profilPanelBody">
          {mesaj&&<div className="profilSuccess">{mesaj}</div>}
          {hata&&<div className="profilError">{hata}</div>}
          {sekme==="bilgi"&&(
            <div className="profilInfoList">
              {[
                [t("profile.fullName"),`${profil?.ad||""} ${profil?.soyad||""}`],
                [t("profile.username"),profil?.kullaniciAdi||"-"],
                [t("profile.role"), translateKnownListValue(tList, profil?.rol) || profil?.rol || "-"],
                [t("profile.email"),profil?.email||"-"],
                [t("profile.phone"),profil?.telefon||"-"],
                [t("profile.birthDate"),profil?.dogumTarihi||"-"],
                ...(!areAndroidHealthFeaturesDisabled() ? [
                  [t("profile.bloodType"),profil?.healthProfile?.bloodType||"-"],
                  [t("profile.height"),profil?.healthProfile?.height||"-"],
                  [t("profile.weight"),profil?.healthProfile?.weight||"-"],
                ] : []),
              ].map(([lbl,val])=>(
                <div key={lbl} className="profilInfoRow"><span className="profilInfoLbl">{lbl}</span><span className="profilInfoVal">{val}</span></div>
              ))}
            </div>
          )}
          {sekme==="duzenle"&&(
            <div className="profilForm">
              <div className="formRow">
                <div className="inputLabelGroup"><label className="darkLabel">{t("profile.name")}</label><input className="darkInput" value={yeniAd} onChange={e=>setYeniAd(e.target.value)} placeholder={t("profile.name")}/></div>
                <div className="inputLabelGroup"><label className="darkLabel">{t("profile.surname")}</label><input className="darkInput" value={yeniSoyad} onChange={e=>setYeniSoyad(e.target.value)} placeholder={t("profile.surname")}/></div>
              </div>
              <div className="inputLabelGroup"><label className="darkLabel">{t("profile.phone")}</label><input className="darkInput" value={yeniTelefon} onChange={e=>setYeniTelefon(e.target.value)} placeholder="+xxxxxxxxxxxx"/></div>
              <div className="inputLabelGroup"><label className="darkLabel">{t("profile.birthDate")}</label><input className="darkInput" type="date" value={yeniDogum} onChange={e=>setYeniDogum(e.target.value)} style={{width:"100%"}}/></div>
              <button className="primaryButton" onClick={profilKaydet} disabled={yukleniyor}>{yukleniyor?t("common.saving"):t("profile.save")}</button>
            </div>
          )}
          {sekme==="sifre"&&(
            <div className="profilForm">
              {!hasPassword && <p className="ayarAciklama">{t("profile.createPasswordHint")}</p>}
              {hasPassword && <div className="inputLabelGroup"><label className="darkLabel">{t("profile.currentPassword")}</label><input className="darkInput" type="password" value={mevcutSifre} onChange={e=>setMevcutSifre(e.target.value)} placeholder="••••••••"/></div>}
              <div className="inputLabelGroup"><label className="darkLabel">{t("profile.newPassword")}</label><input className="darkInput" type="password" minLength={8} maxLength={128} value={yeniSifre} onChange={e=>setYeniSifre(e.target.value)} placeholder={t("auth.placeholder.passwordMin")}/></div>
              <div className="inputLabelGroup"><label className="darkLabel">{t("profile.newPasswordRepeat")}</label><input className="darkInput" type="password" minLength={8} maxLength={128} value={yeniSifreTekrar} onChange={e=>setYeniSifreTekrar(e.target.value)} placeholder="••••••••"/></div>
              <button className="primaryButton" onClick={sifreKaydet} disabled={yukleniyor}>{yukleniyor?t("common.updating"):t(hasPassword ? "profile.changePassword" : "profile.createPassword")}</button>
            </div>
          )}
          {sekme==="avatar"&&(
            <div className="avatarSekmeWrap">
              <div className="avatarOnizlemeRow">
                <button
                  type="button"
                  className="avatarOnizlemeButton"
                  onClick={() => setAvatarPreviewOpen(true)}
                  aria-label={lang === "en" ? "View selected avatar" : "Seçili avatarı görüntüle"}
                >
                  <AvatarImg
                    avatar={currentAvatar}
                    renk={kullaniciRenk}
                    initials={initials || "FP"}
                    size={72}
                    glow={true}
                    radius="50%"
                  />
                  <span className="avatarPreviewBadge" aria-hidden="true">⌕</span>
                </button>
                <div className="avatarOnizlemeBilgi">
                  <span className="avatarOnizlemeAd">{profil?.ad} {profil?.soyad}</span>
                  <span className="avatarOnizlemeRol">{translateKnownListValue(tList, profil?.rol) || profil?.rol}</span>
                </div>
              </div>
              <div className="profilePhotoQuickPanel">
                <div className="profilePhotoQuickActions">
                  <button type="button" onClick={() => kameraInputRef.current?.click()} disabled={avatarYukleniyor || fotoHazirlaniyor}>
                    <span aria-hidden="true">📷</span>{t("profile.photo.camera")}
                  </button>
                  <button type="button" onClick={() => galeriInputRef.current?.click()} disabled={avatarYukleniyor || fotoHazirlaniyor}>
                    <span aria-hidden="true">▣</span>{t("profile.photo.gallery")}
                  </button>
                  {storedProfilePhoto && (
                    <button className="profilePhotoQuickRemove" type="button" onClick={fotografiKaldir} disabled={avatarYukleniyor || fotoHazirlaniyor} title={t("profile.photo.remove")} aria-label={t("profile.photo.remove")}>
                      <span aria-hidden="true">×</span>
                    </button>
                  )}
                </div>
                <input
                  ref={kameraInputRef}
                  className="profilePhotoHiddenInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  capture="user"
                  onChange={fotoDosyasiSecildi}
                />
                <input
                  ref={galeriInputRef}
                  className="profilePhotoHiddenInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  onChange={fotoDosyasiSecildi}
                />
                {avatarYukleniyor && fotoYuklemeYuzdesi > 0 && (
                  <div className="profilePhotoProgress" aria-live="polite">
                    <span style={{ width: `${fotoYuklemeYuzdesi}%` }} />
                    <small>{t("profile.photo.uploading", { percent: fotoYuklemeYuzdesi })}</small>
                  </div>
                )}
                {fotoHazirlaniyor && <div className="profilePhotoPreparing" aria-live="polite">{t("profile.photo.preparing")}</div>}
              </div>
              <div className="avatarRenkPanel">
                <div className="avatarGrupBaslik">{t("profile.avatar.colors")}</div>
                <div className="avatarRenkPalette" aria-label={t("profile.avatar.colors")}>
                  {RENKLER.map(r => (
                    <button
                      key={r}
                      type="button"
                      className={`avatarRenkDot${kullaniciRenk === r ? " secili" : ""}`}
                      style={{ background: r, "--avatar-renk": r }}
                      onClick={() => renkSec(r)}
                      title={r}
                    />
                  ))}
                </div>
              </div>

              {(onerilens.length > 0 || storedProfilePhoto) && (
                <>
                  <div className="avatarGrupBaslik">{t("profile.avatar.hint")}</div>
                  <div className="avatarGrid">
                    <button
                      type="button"
                      className={`avatarGridItem avatarInitialsChoice${!currentAvatar ? " secili" : ""}`}
                      style={!currentAvatar ? { boxShadow: `0 0 0 2.5px ${kullaniciRenk}, 0 0 12px ${kullaniciRenk}66` } : {}}
                      onClick={() => avatarSec(null)}
                      title={t("profile.avatar.initials")}
                    >
                      <AvatarImg
                        avatar={null}
                        renk={kullaniciRenk}
                        initials={initials || "FP"}
                        size={58}
                        glow={true}
                        radius="50%"
                      />
                    </button>
                    {storedProfilePhoto && (
                      <button
                        type="button"
                        className={`avatarGridItem profilePhotoAvatarChoice${currentAvatar === storedProfilePhoto ? " secili" : ""}`}
                        style={currentAvatar === storedProfilePhoto ? { boxShadow: `0 0 0 2.5px ${kullaniciRenk}, 0 0 12px ${kullaniciRenk}66` } : {}}
                        onClick={() => avatarSec(storedProfilePhoto)}
                        title={t("profile.photo.title")}
                      >
                        <AvatarImg
                          avatar={storedProfilePhoto}
                          renk={kullaniciRenk}
                          initials={initials || "FP"}
                          size={58}
                          radius="50%"
                        />
                      </button>
                    )}
                    {onerilens.map(av => (
                      <button
                        key={av.file}
                        type="button"
                        className={`avatarGridItem${currentAvatar === av.file ? " secili" : ""}`}
                        style={currentAvatar === av.file ? { boxShadow: `0 0 0 2.5px ${kullaniciRenk}, 0 0 12px ${kullaniciRenk}66` } : {}}
                        onClick={() => avatarSec(av.file)}
                        title={av.label}
                      >
                        <img src={AVATAR_IMPORTS[av.file] || ""} alt={av.label} />
                      </button>
                    ))}
                  </div>
                </>
              )}
              {digerler.length > 0 && (
                <>
                  <div className="avatarGrupBaslik">{t("profile.avatar.all")}</div>
                  <div className="avatarGrid">
                    {digerler.map(av => (
                      <button
                        key={av.file}
                        type="button"
                        className={`avatarGridItem${currentAvatar === av.file ? " secili" : ""}`}
                        style={currentAvatar === av.file ? { boxShadow: `0 0 0 2.5px ${kullaniciRenk}, 0 0 12px ${kullaniciRenk}66` } : {}}
                        onClick={() => avatarSec(av.file)}
                        title={av.label}
                      >
                        <img src={AVATAR_IMPORTS[av.file] || ""} alt={av.label} />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="profilPanelFooter">
          <button className="profilCikisBtn" onClick={cikisYapOnayli}><PanelIcon type="logout" /> {t("profile.logout")}</button>
          <button className="profilKapatBtn" onClick={kapat}>{t("common.close")}</button>
        </div>
      </div>
      {fotoEditorDosyasi && (
        <ProfilePhotoEditor
          file={fotoEditorDosyasi}
          renk={kullaniciRenk}
          t={t}
          saving={avatarYukleniyor}
          onCancel={() => !avatarYukleniyor && setFotoEditorDosyasi(null)}
          onSave={kirpilmisFotografiKaydet}
        />
      )}
      <AvatarPreviewModal
        open={avatarPreviewOpen}
        onClose={() => setAvatarPreviewOpen(false)}
        avatar={currentAvatar}
        renk={kullaniciRenk}
        initials={initials || "FP"}
        title={lang === "en" ? "Profile avatar" : "Profil avatarı"}
        closeLabel={t("common.close")}
      />
    </div>
  );
}

/* ─────────────────────────────────────────
   AYARLAR PANELİ
───────────────────────────────────────── */
const RENKLER = ["#2563eb","#7c3aed","#db2777","#ea580c","#16a34a","#0891b2","#ca8a04","#64748b","#0f172a","#dc2626"];

/* Avatar listesi — dosya adı + etiket + önerilen roller */
const AVATAR_LIST = [
  { file: "1anne3.png",    label: "Anne",    roles: ["Anne"] },
  { file: "2anne1.png",    label: "Anne",    roles: ["Anne"] },
  { file: "3anne2.png",    label: "Anne",    roles: ["Anne"] },
  { file: "4teyze.png",    label: "Teyze",   roles: ["Teyze", "Anne"] },
  { file: "5nene.png",     label: "Nene",    roles: ["Nene"] },
  { file: "6baba5.png",    label: "Baba",    roles: ["Baba"] },
  { file: "7baba3.png",    label: "Baba",    roles: ["Baba"] },
  { file: "8baba4.png",    label: "Baba",    roles: ["Baba"] },
  { file: "9baba2.png",    label: "Baba",    roles: ["Baba"] },
  { file: "10baba1.png",   label: "Baba",    roles: ["Baba", "Aile Yöneticisi"] },
  { file: "11dayi.png",    label: "Dayı",    roles: ["Dayı", "Amca"] },
  { file: "12amca.png",    label: "Amca",    roles: ["Amca", "Dayı"] },
  { file: "13dede.png",    label: "Dede",    roles: ["Dede"] },
  { file: "14cocuk2.png",  label: "Çocuk",   roles: ["Çocuk"] },
  { file: "15cocuk4.png",  label: "Çocuk",   roles: ["Çocuk"] },
  { file: "16cocuk1.png",  label: "Çocuk",   roles: ["Çocuk", "Aile Üyesi", "Üye"] },
  { file: "17cocuk3.png",  label: "Çocuk",   roles: ["Çocuk"] },
];
// SAYFALAR AyarlarPanel içinde t() ile oluşturulur

function AyarlarPanel({
  profil, tema, setTema, paraBirimi, setParaBirimi,
  bildirimAcik, setBildirimAcik, bildirimGun, setBildirimGun,
  varsayilanSayfa, setVarsayilanSayfa,
  alisverisHatirlatici, setAlisverisHatirlatici,
  familyId, familyName, yoneticiMi,
  aileAdiGuncelle, uyeler, uyeRenkGuncelle, hesapSil, kapat,
  familyStatusVisible, onFamilyStatusVisibleChange,
  sosyalHesapBagla,
  dilGuncelle, verileriDisaAktar,
  isSuperAdmin = false,
  onOpenLegal,
  onOpenFeedback,
  onStartAppTour,
}) {
  const { t, lang } = useLanguage();
  const SAYFALAR = [
    { v: "ana", label: t("nav.home"), icon: ailemAsset },
    { v: "assistant", label: t("nav.assistant"), icon: aileAsistaniAsset },
    { v: "alisveris", label: t("nav.shopping"), icon: alisverisAsset },
    { v: "takvim", label: t("nav.calendar"), icon: takvimAsset },
    { v: "finans", label: t("nav.finance"), icon: finansAsset },
    { v: "gorev", label: t("nav.tasks"), icon: gorevlerAsset },
    { v: "harcama", label: t("nav.sharedExpenses"), icon: harcamalarAsset },
    { v: "saglik", label: t("nav.health"), icon: saglikAsset },
    { v: "harita", label: t("nav.map"), icon: aileHaritasiAsset },
    { v: "cocuk", label: t("nav.children"), icon: cocukTakibiAsset },
    { v: "chat", label: t("nav.chat"), icon: aileSohbetiAsset },
    { v: "belgeler", label: t("nav.documents"), icon: belgelerAsset },
  ].filter(({ v }) => !isAndroidRestrictedPage(v));
  const [yeniAileAdi, setYeniAileAdi] = useState(familyName);
  const [silOnay, setSilOnay] = useState(false);
  const [silSifre, setSilSifre] = useState("");
  const [exportYukleniyor, setExportYukleniyor] = useState(false);
  const [silYukleniyor, setSilYukleniyor] = useState(false);
  const [konumGecmisiSilYukleniyor, setKonumGecmisiSilYukleniyor] = useState(false);
  const [sosyalBaglamaYukleniyor, setSosyalBaglamaYukleniyor] = useState("");
  const [bolum, setBolum] = useState("genel");
  const [animClass, setAnimClass] = useState("");
  const ayarlarTabsRef = useRef(null);
  const ayarlarTabsScrollTargetRef = useRef(null);
  const [ayarSekmeKaydirma, setAyarSekmeKaydirma] = useState({ sol: false, sag: false });
  const bolumSirasi = ["genel", "bildirim", ...(familyId ? ["aile"] : []), "gizlilik", "hakkinda"];
  const [aileBasari, setAileBasari] = useState("");
  const [runtimeVersion, setRuntimeVersion] = useState("");
  const [notificationSettings, setNotificationSettings] = useState(
  DEFAULT_NOTIFICATION_SETTINGS
);

  const [bildirimAyarYukleniyor] = useState(false);
  const hasPassword = profil?.hasPassword !== false;
  const authProviders = Array.isArray(profil?.authProviders) ? profil.authProviders : (hasPassword ? ["password"] : []);
  const socialProviders = authProviders.filter((provider) => provider === "google.com" || provider === "apple.com");
  const appleLinked = authProviders.includes("apple.com");
  const usePasswordForDeletion = hasPassword && !appleLinked;
  const deletionSocialProviders = appleLinked ? ["apple.com"] : socialProviders;

  const hesabiKaliciSil = async ({ password = "", provider = "" } = {}) => {
    if (usePasswordForDeletion && !password) {
      await appAlert(t("settings.privacy.passwordRequired"), { modal: true, title: t("settings.privacy.deleteAccount") });
      return;
    }
    const onay = await appConfirm(t("settings.privacy.finalDeleteWarning"), {
      title: t("settings.privacy.deleteAccount"),
      confirmText: t("settings.privacy.confirmDelete"),
      cancelText: t("common.cancel"),
      danger: true,
    });
    if (!onay) return;
    try {
      setSilYukleniyor(true);
      await hesapSil(password, provider);
      await appAlert(t("settings.privacy.deleteSuccess"), { modal: true, title: t("settings.privacy.deleteAccount") });
      kapat?.();
    } catch (e) {
      const code = e?.code;
      const blockedFamilies = Array.isArray(e?.payload?.families) ? e.payload.families.filter(Boolean) : [];
      const familySuffix = blockedFamilies.length ? `\n\n${blockedFamilies.join(", ")}` : "";
      const message = code === "account_deletion_requires_admin_transfer"
        ? `${t("settings.privacy.adminTransferRequired")}${familySuffix}`
        : code === "invalid_password"
          ? t("settings.privacy.invalidPassword")
          : (e?.message || t("settings.privacy.deleteFailed"));
      await appAlert(message, { modal: true, title: t("settings.privacy.deleteAccount") });
    } finally {
      setSilYukleniyor(false);
    }
  };

const [, setBildirimKaydediliyor] = useState(false);
const [bildirimMesaj, setBildirimMesaj] = useState("");
const [pushDurum, setPushDurum] = useState("kontrol"); // kontrol | aktif | pasif | desteklenmiyor
const [pushAktifYukleniyor, setPushAktifYukleniyor] = useState(false);
const settingsFamilyId = familyId || profil?.familyId;
// Ağ isteği `/settings/me` üzerinden JWT kullanıcısına bağlanır. Buradaki UID
// yalnız yerel cache/pending anahtarıdır; AyarlarPanel'in mevcut profil prop'u
// dışındaki üst bileşen değişkenlerine bağımlı olmamalıdır.
const settingsUid = profil?.firebase_uid || profil?.uid || profil?.id;

  useEffect(() => {
    let active = true;
    getRuntimeVersionLabel()
      .then((value) => { if (active) setRuntimeVersion(value); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const yasalSayfaAc = (path) => {
    const documentKey = String(path || "").replace(/^\/+/, "");
    kapat?.();

    // Uygulama içindeyken tam sayfa yönlendirme yapma. Böylece MainApp
    // unmount olmaz, startup animasyonu tekrar çalışmaz ve global sayfa
    // geçiş/swipe-to-back altyapısı aynen korunur.
    if (typeof onOpenLegal === "function") {
      onOpenLegal(documentKey);
      return;
    }

    // Yasal sayfalar web üzerinden doğrudan erişilebilir kalır.
    const langPrefix = lang === "en" ? "/en" : "";
    window.location.assign(`${langPrefix}/${documentKey}`);
  };

  const BOLUMLER = [
    {v:"genel",l:t("settings.tab.general"),icon:"general"},
    {v:"bildirim",l:t("settings.tab.notifications"),icon:"notifications"},
    ...(familyId ? [{v:"aile",l:t("settings.tab.family"),icon:"family"}] : []),
    {v:"gizlilik",l:t("settings.tab.privacy"),icon:"privacy"},
    {v:"hakkinda",l:t("settings.tab.about"),icon:"about"},
  ];

  const setBolumAnimasyonlu = (yeniBolum) => {
    if (yeniBolum === bolum) return;
    const mevcutIdx = bolumSirasi.indexOf(bolum);
    const yeniIdx   = bolumSirasi.indexOf(yeniBolum);
    const yon = yeniIdx > mevcutIdx ? "slide-from-right" : "slide-from-left";
    setAnimClass("");
    requestAnimationFrame(() => {
      setBolum(yeniBolum);
      setAnimClass(yon);
    });
  };

  useEffect(() => {
    const tabs = ayarlarTabsRef.current;
    if (!tabs) return undefined;

    const kaydirmaDurumunuGuncelle = () => {
      const maxScroll = Math.max(0, tabs.scrollWidth - tabs.clientWidth);
      const normalizedScrollLeft = Math.max(0, Math.min(maxScroll, tabs.scrollLeft));
      if (ayarlarTabsScrollTargetRef.current !== null
        && Math.abs(normalizedScrollLeft - ayarlarTabsScrollTargetRef.current) <= 2) {
        ayarlarTabsScrollTargetRef.current = null;
      }
      setAyarSekmeKaydirma({
        sol: normalizedScrollLeft > 4,
        sag: normalizedScrollLeft < maxScroll - 4,
      });
    };

    const frame = requestAnimationFrame(kaydirmaDurumunuGuncelle);
    tabs.addEventListener("scroll", kaydirmaDurumunuGuncelle, { passive: true });
    window.addEventListener("resize", kaydirmaDurumunuGuncelle);
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(kaydirmaDurumunuGuncelle)
      : null;
    resizeObserver?.observe(tabs);

    return () => {
      cancelAnimationFrame(frame);
      tabs.removeEventListener("scroll", kaydirmaDurumunuGuncelle);
      window.removeEventListener("resize", kaydirmaDurumunuGuncelle);
      resizeObserver?.disconnect();
    };
  }, [familyId, lang]);

  const ayarSekmeleriniKaydir = (yon) => {
    const tabs = ayarlarTabsRef.current;
    if (!tabs) return;
    const maxScroll = Math.max(0, tabs.scrollWidth - tabs.clientWidth);
    const currentTarget = ayarlarTabsScrollTargetRef.current ?? tabs.scrollLeft;
    const nextTarget = Math.max(0, Math.min(
      maxScroll,
      currentTarget + yon * Math.max(100, tabs.clientWidth * 0.52)
    ));
    ayarlarTabsScrollTargetRef.current = nextTarget;
    tabs.scrollTo({
      left: nextTarget,
      behavior: "smooth",
    });
  };

  /* ── Swipe ile sekme geçişi ── */
  const swipeTouchStartX = useRef(0);
  const swipeTouchStartY = useRef(0);
  const swipeHedefRef    = useRef(null);

  const onSwipeTouchStart = (e) => {
    // Slide bar (range input) veya data-no-swipe elementlerine dokunulmuşsa atla
    const hedef = e.target;
    if (
      hedef?.tagName === "INPUT" ||
      hedef?.closest?.(".ayarlarTabs") ||
      hedef?.closest?.("input[type=range]") ||
      hedef?.closest?.("[data-no-swipe]")
    ) {
      swipeHedefRef.current = null;
      return;
    }
    swipeHedefRef.current = hedef;
    swipeTouchStartX.current = e.touches[0].clientX;
    swipeTouchStartY.current = e.touches[0].clientY;
  };

  const onSwipeTouchEnd = (e) => {
    if (!swipeHedefRef.current) return;
    const dx = e.changedTouches[0].clientX - swipeTouchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - swipeTouchStartY.current);
    // Yatay > 55px, yatay > dikey — yeterli swipe
    if (Math.abs(dx) < 55 || Math.abs(dx) < dy) return;
    const mevcutIdx = bolumSirasi.indexOf(bolum);
    if (dx < 0 && mevcutIdx < bolumSirasi.length - 1) {
      // Sola kaydır → sonraki sekme
      setBolumAnimasyonlu(bolumSirasi[mevcutIdx + 1]);
    } else if (dx > 0 && mevcutIdx > 0) {
      // Sağa kaydır → önceki sekme
      setBolumAnimasyonlu(bolumSirasi[mevcutIdx - 1]);
    }
  };

      useEffect(() => {
  let aktif = true;

  async function yukle() {
    try {
      if (!settingsUid || !settingsFamilyId) return;

      const data = await getMyNotificationSettings(
        settingsFamilyId,
        settingsUid
      );
      const permission = await getPushPermissionStatus();

      if (aktif) {
        setNotificationSettings(
          permission === "granted" ? data : disableAllNotificationSettings(data)
        );
      }
    } catch (err) {
      console.error("Bildirim ayarları yüklenemedi:", err);
    }
  }

  yukle();

  return () => {
    aktif = false;
  };
}, [settingsUid, settingsFamilyId]);

    /* Push bildirim durumunu kontrol et */
  useEffect(() => {
    const kontrol = async () => {
      const izin = await getPushPermissionStatus();
      setPushDurum(izin === "granted" ? "aktif" : izin === "unsupported" ? "desteklenmiyor" : "pasif");
      return izin;
    };
    kontrol();
    const onChange = async () => {
      const permission = await kontrol().catch(() => "unsupported");
      if (settingsFamilyId && settingsUid) {
        getMyNotificationSettings(settingsFamilyId, settingsUid)
          .then((settings) => setNotificationSettings(
            permission === "granted" ? settings : disableAllNotificationSettings(settings)
          ))
          .catch(() => {});
      }
    };
    window.addEventListener(PUSH_PERMISSION_EVENT, onChange);
    return () => window.removeEventListener(PUSH_PERMISSION_EVENT, onChange);
  }, [settingsFamilyId, settingsUid]);

  /* Push bildirimleri aktifleştir */
  const pushAktifleştir = async () => {
    setPushAktifYukleniyor(true);
    try {
      const before = await getPushPermissionStatus();
      if (before === "denied") {
        setPushEnablePending(settingsUid, true);
        const opened = await openPushSettings();
        setBildirimMesaj(opened ? t("settings.notifications.openSystemSettings") : t("settings.notifications.permissionDenied"));
        return;
      }

      const status = await requestPushAccess({ user: profil, familyId: settingsFamilyId, lang });
      if (status !== "granted") {
        setPushDurum("pasif");
        setBildirimMesaj(t("settings.notifications.permissionDenied"));
        return;
      }

      const yeniAyarlar = enableAllNotificationSettings(notificationSettings);
      await saveMyNotificationSettings(settingsFamilyId, settingsUid, yeniAyarlar);
      setNotificationSettings(yeniAyarlar);
      setPushEnablePending(settingsUid, false);
      setPushDurum("aktif");
      setBildirimMesaj(t("settings.notifications.pushActivated"));
      setTimeout(() => setBildirimMesaj(""), 3000);
    } catch (err) {
      setBildirimMesaj(err?.message || t("settings.notifications.saveFailed"));
      setTimeout(() => setBildirimMesaj(""), 5000);
    } finally {
      setPushAktifYukleniyor(false);
    }
  };

  const tumBildirimleriDegistir = async () => {
    if (pushAktifYukleniyor) return;
    if (!notificationSettings.pushEnabled) {
      await pushAktifleştir();
      return;
    }
    setPushEnablePending(settingsUid, false);
    const oncekiAyarlar = notificationSettings;
    const yeniAyarlar = disableAllNotificationSettings(notificationSettings);
    setNotificationSettings(yeniAyarlar);
    try {
      await saveMyNotificationSettings(settingsFamilyId, settingsUid, yeniAyarlar);
    } catch (error) {
      setNotificationSettings(oncekiAyarlar);
      setBildirimMesaj(error?.message || t("settings.notifications.saveFailed"));
    }
  };

  const bildirimAyarGuncelle = async (key, value) => {
    const oncekiAyarlar = notificationSettings;
    try {
      const yeniAyarlar = {
        ...notificationSettings,
        [key]: value,
      };

      setNotificationSettings(yeniAyarlar);

      setBildirimKaydediliyor(true);

      await saveMyNotificationSettings(
        settingsFamilyId,
        settingsUid,
        yeniAyarlar
      );
    } catch (err) {
      console.error(err);
      setNotificationSettings(oncekiAyarlar);
      setBildirimMesaj(err?.message || t("settings.notifications.saveFailed"));
    } finally {
      setBildirimKaydediliyor(false);
    }
  };

  return (
    <div
      className={`profilPanelOverlay${Capacitor.getPlatform() === "android" ? " profilPanelOverlay--android-stable" : ""}`}
      onClick={kapat}
    >
      <div
        className="profilPanel ayarlarPanel"
        onClick={e=>e.stopPropagation()}
        onTouchStart={onSwipeTouchStart}
        onTouchEnd={onSwipeTouchEnd}
      >
        <div className="ayarlarHeader">
          <span className="ayarlarTitle">{t("settings.title")}</span>
          <button className="ayarKapatBtn" onClick={kapat}>×</button>
        </div>
          <div className={`ayarlarTabsShell ${ayarSekmeKaydirma.sol ? "canScrollLeft" : ""} ${ayarSekmeKaydirma.sag ? "canScrollRight" : ""}`} data-no-swipe>
          {ayarSekmeKaydirma.sol && (
            <button
              type="button"
              className="ayarlarTabsKaydir ayarlarTabsKaydir--sol"
              onClick={() => ayarSekmeleriniKaydir(-1)}
              aria-label={lang === "en" ? "Show previous settings tabs" : "Önceki ayar sekmelerini göster"}
            >‹</button>
          )}
          <div
            className="ayarlarTabs"
            ref={ayarlarTabsRef}
            onPointerDown={() => { ayarlarTabsScrollTargetRef.current = null; }}
            onTouchStart={() => { ayarlarTabsScrollTargetRef.current = null; }}
          >
            {BOLUMLER.map(b=>(
              <button
                key={b.v}
                className={bolum===b.v?"ayarlarTab active":"ayarlarTab"}
                onClick={(event)=>{
                  ayarlarTabsScrollTargetRef.current = null;
                  event.currentTarget.scrollIntoView?.({ behavior:"smooth", block:"nearest", inline:"nearest" });
                  setBolumAnimasyonlu(b.v);
                }}
              ><PanelIcon type={b.icon} />{b.l}</button>
            ))}
          </div>
          {ayarSekmeKaydirma.sag && (
            <button
              type="button"
              className="ayarlarTabsKaydir ayarlarTabsKaydir--sag"
              onClick={() => ayarSekmeleriniKaydir(1)}
              aria-label={lang === "en" ? "Show more settings tabs" : "Diğer ayar sekmelerini göster"}
            >›</button>
          )}
        </div>
        <div className="profilPanelBody">
          <div className={`ayarlarIcerik ${animClass}`} key={bolum}>

          {/* ── GENEL ── */}
          {bolum==="genel"&&(
            <>
              <div className="ayarGrup">
                <div className="ayarGrupBaslik">{t("settings.theme")}</div>
                <div className="ayarToggleRow">
                  {[["dark",t("settings.theme.dark")],["light",t("settings.theme.light")]].map(([v,l])=>(
                    <button key={v} className={tema===v?"ayarToggleBtn active":"ayarToggleBtn"} onClick={()=>setTema(v)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="ayarGrup">
                <div className="ayarGrupBaslik">{t("settings.currency")}</div>
                <div className="ayarToggleRow">
                  {[["TRY","₺ TL"],["USD","$ USD"],["EUR","€ EUR"]].map(([v,l])=>(
                    <button key={v} className={paraBirimi===v?"ayarToggleBtn active":"ayarToggleBtn"} onClick={()=>setParaBirimi(v)}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="ayarGrup">
                <div className="ayarGrupBaslik">{t("settings.defaultPage")}</div>
                <div className="defaultPageList" role="radiogroup" aria-label={t("settings.defaultPage")} data-no-swipe="true">
                  {SAYFALAR.map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      className={`defaultPageOption ${varsayilanSayfa === s.v ? "active" : ""}`}
                      onClick={() => setVarsayilanSayfa(s.v)}
                      role="radio"
                      aria-checked={varsayilanSayfa === s.v}
                    >
                      <span className="defaultPageIconWrap" aria-hidden="true">
                        <img src={s.icon} alt="" className="defaultPageIcon" />
                      </span>
                      <span className="defaultPageLabel">{s.label}</span>
                      <span className="defaultPageCheck" aria-hidden="true">✓</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* ── Dil Seçici ── */}
              <LanguageSwitcher variant="settings" onChangeLang={dilGuncelle} />
            </>
          )}

          {/* ── BİLDİRİM ── */}
{bolum === "bildirim" && (
  <>
    {bildirimAyarYukleniyor ? (
      <div className="ayarGrup">
        <div className="ayarGrupBaslik">{t("settings.notifications.loading")}</div>
        <p className="ayarAciklama">{t("settings.notifications.loadingHint")}</p>
      </div>
    ) : (
      <>
                <div className="ayarGrup">
          <div className="ayarGrupBaslik">{t("settings.notifications.masterControl")}</div>

          <label className="toggleLine">
            <span className="toggleLabelText">{t("settings.notifications.allNotifications")}</span>
            <div
              className={`toggleSwitch ${notificationSettings.pushEnabled ? "on" : ""}`}
              onClick={tumBildirimleriDegistir}
            >
              <div className="toggleThumb" />
            </div>
          </label>

          <p className="ayarAciklama">
            {t("settings.notifications.allHint").replace(/push /gi, "").replace(/Push /gi, "")}
          </p>
          <div className={`ayarBildirimIzinDurumu ${pushDurum === "aktif" ? "aktif" : "pasif"}`}>
            <span aria-hidden="true" />
            {pushDurum === "aktif" ? t("notification.permissionOn") : t("notification.permissionOff")}
          </div>
          {bildirimMesaj && <p className="ayarAciklama" role="status">{bildirimMesaj}</p>}
        </div>

        <div className="ayarGrup compactToggleGroup">
          <div className="ayarGrupBaslik">{t("settings.notifications.categories")}</div>

          {[
            ["chat", t("settings.notifications.cat.chat")],
            ["tasks", t("settings.notifications.cat.tasks")],
            ["calendar", t("settings.notifications.cat.calendar")],
            ["health", t("settings.notifications.cat.health")],
            ["shopping", t("settings.notifications.cat.shopping")],
            ["sharedExpenses", t("settings.notifications.cat.sharedExpenses")],
            ["finance", t("settings.notifications.cat.finance")],
            ["documents", t("settings.notifications.cat.documents")],
            ["documentExpiry", t("settings.notifications.cat.documentExpiry")],
            ["familyAnnouncements", t("settings.notifications.cat.familyAnnouncements")],
            ["birthdayNotifications", t("settings.notifications.cat.birthdays")],
            ["familyStatus", t("settings.notifications.cat.familyStatus")],
          ].filter(([key]) => !areAndroidHealthFeaturesDisabled() || key !== "health").map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings[key] ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle(key, !notificationSettings[key])}
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>

        <div className="ayarGrup">
          <div className="ayarGrupBaslik">{t("settings.notifications.quietHours")}</div>

          <label className="toggleLine">
            <span className="toggleLabelText">{t("settings.notifications.quietHoursToggle")}</span>
            <div
              className={`toggleSwitch ${notificationSettings.quietHoursEnabled ? "on" : ""}`}
              onClick={() =>
                bildirimAyarGuncelle(
                  "quietHoursEnabled",
                  !notificationSettings.quietHoursEnabled
                )
              }
            >
              <div className="toggleThumb" />
            </div>
          </label>

          <p className="ayarAciklama">
            {t("settings.notifications.quietHoursHint")}
          </p>

          {notificationSettings.quietHoursEnabled && (
            <div className="formRow" style={{ marginTop: "12px" }}>
              <div className="inputLabelGroup">
                <label className="darkLabel">{t("settings.notifications.quietStart")}</label>
                <input
                  type="time"
                  className="darkInput"
                  value={notificationSettings.quietStart || "22:00"}
                  onChange={(e) =>
                    bildirimAyarGuncelle("quietStart", e.target.value)
                  }
                />
              </div>

              <div className="inputLabelGroup">
                <label className="darkLabel">{t("settings.notifications.quietEnd")}</label>
                <input
                  type="time"
                  className="darkInput"
                  value={notificationSettings.quietEnd || "08:00"}
                  onChange={(e) =>
                    bildirimAyarGuncelle("quietEnd", e.target.value)
                  }
                />
              </div>
            </div>
          )}
        </div>

        <div className={`ayarGrup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.autoReminders")}</div>
          <div className="inputLabelGroup" style={{ marginTop: "12px" }}>
            <label className="darkLabel">{t("settings.notifications.shoppingReminder")}</label>
            <select
              className="darkInput"
              disabled={!notificationSettings.pushEnabled}
              value={notificationSettings.shoppingReminder?.frequency || "daily"}
              onChange={(e) =>
                bildirimAyarGuncelle("shoppingReminder", {
                  ...DEFAULT_NOTIFICATION_SETTINGS.shoppingReminder,
                  ...(notificationSettings.shoppingReminder || {}),
                  enabled: e.target.value !== "off",
                  frequency: e.target.value,
                })
              }
            >
              <option value="off">{t("settings.notifications.freq.off")}</option>
              <option value="6hours">{t("settings.notifications.freq.6hours")}</option>
              <option value="daily">{t("settings.notifications.freq.daily")}</option>
              <option value="2days">{t("settings.notifications.freq.2days")}</option>
              <option value="weekly">{t("settings.notifications.freq.weekly")}</option>
            </select>
            <p className="ayarAciklama">
              {t("settings.notifications.shoppingReminderHint")}
            </p>
          </div>
        </div>

        <div className={`ayarGrup compactToggleGroup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.taskReminders")}</div>

          {[
            ["today", t("settings.notifications.task.today")],
            ["tomorrow", t("settings.notifications.task.tomorrow")],
            ["threeDaysBefore", t("settings.notifications.task.threeDays")],
          ].map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings.taskReminder?.[key] !== false ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle("taskReminder", {
                    ...DEFAULT_NOTIFICATION_SETTINGS.taskReminder,
                    ...(notificationSettings.taskReminder || {}),
                    [key]: notificationSettings.taskReminder?.[key] === false,
                  })
                }
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>

        <div className={`ayarGrup compactToggleGroup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.calendarReminders")}</div>

          {[
            ["today", t("settings.notifications.calendar.today")],
            ["tomorrow", t("settings.notifications.calendar.tomorrow")],
          ].map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings.calendarReminder?.[key] !== false ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle("calendarReminder", {
                    ...DEFAULT_NOTIFICATION_SETTINGS.calendarReminder,
                    ...(notificationSettings.calendarReminder || {}),
                    [key]: notificationSettings.calendarReminder?.[key] === false,
                  })
                }
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>

        <div className={`ayarGrup compactToggleGroup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.docReminders")}</div>

          {[
            ["oneDayBefore", t("settings.notifications.doc.oneDay")],
            ["sevenDaysBefore", t("settings.notifications.doc.sevenDays")],
            ["thirtyDaysBefore", t("settings.notifications.doc.thirtyDays")],
          ].map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings.documentReminder?.[key] !== false ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle("documentReminder", {
                    ...DEFAULT_NOTIFICATION_SETTINGS.documentReminder,
                    ...(notificationSettings.documentReminder || {}),
                    [key]: notificationSettings.documentReminder?.[key] === false,
                  })
                }
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>

        {!areAndroidHealthFeaturesDisabled() && <div className={`ayarGrup compactToggleGroup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.healthReminders")}</div>

          {[
            ["appointments", t("settings.notifications.health.appointments")],
            ["medicines", t("settings.notifications.health.medicines")],
          ].map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings.healthReminder?.[key] !== false ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle("healthReminder", {
                    ...DEFAULT_NOTIFICATION_SETTINGS.healthReminder,
                    ...(notificationSettings.healthReminder || {}),
                    [key]: notificationSettings.healthReminder?.[key] === false,
                  })
                }
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>}

        <div className={`ayarGrup compactToggleGroup${!notificationSettings.pushEnabled ? " notificationSubSettingsDisabled" : ""}`}>
          <div className="ayarGrupBaslik">{t("settings.notifications.financeReminders")}</div>

          {[
            ["installments", t("settings.notifications.finance.installments")],
            ["subscriptions", t("settings.notifications.finance.subscriptions")],
            ["bills", t("settings.notifications.finance.bills")],
            ["savings", t("settings.notifications.finance.savings")],
          ].map(([key, label]) => (
            <label className={`toggleLine${!notificationSettings.pushEnabled ? " disabled" : ""}`} key={key}>
              <span className="toggleLabelText">{label}</span>
              <div
                className={`toggleSwitch ${notificationSettings.financeReminder?.[key] !== false ? "on" : ""}`}
                onClick={() => notificationSettings.pushEnabled &&
                  bildirimAyarGuncelle("financeReminder", {
                    ...DEFAULT_NOTIFICATION_SETTINGS.financeReminder,
                    ...(notificationSettings.financeReminder || {}),
                    [key]: notificationSettings.financeReminder?.[key] === false,
                  })
                }
              >
                <div className="toggleThumb" />
              </div>
            </label>
          ))}
        </div>
      </>
    )}
  </>
)}

          {/* ── AİLE ── */}
          {bolum==="aile"&&(
            <>
              <div className={`ayarGrup familyStatusSettingsGroup${yoneticiMi ? " familyStatusSettingsGroupAdmin" : ""}`}>
                <div className="familyStatusSettingsHeader">
                  <div>
                    <div className="ayarGrupBaslik">{yoneticiMi ? t("settings.family.rename") : (lang === "en" ? "Family status" : "Aile durumu")}</div>
                    {!yoneticiMi && <small>{lang === "en" ? "Show the family status card on the home screen" : "Ana sayfada aile durumu kartını göster"}</small>}
                  </div>
                  <div className="familyStatusSettingsToggle" data-no-swipe>
                    <span>{lang === "en" ? "Status card" : "Durum kartı"}</span>
                    <button type="button" className={`toggleSwitch ${familyStatusVisible ? "on" : ""}`} onClick={() => onFamilyStatusVisibleChange?.(!familyStatusVisible)} aria-pressed={familyStatusVisible} aria-label={lang === "en" ? "Show family status card" : "Aile durumu kartını göster"}>
                      <span className="toggleThumb" />
                    </button>
                  </div>
                </div>
                {yoneticiMi && (
                  <>
                    <div className="formRow">
                      <input className="darkInput" value={yeniAileAdi} onChange={e=>setYeniAileAdi(e.target.value)} placeholder={t("settings.family.namePlaceholder")}/>
                      <button className="addButton" onClick={async()=>{await aileAdiGuncelle(yeniAileAdi);setAileBasari(t("settings.family.renameSuccess"));setTimeout(()=>setAileBasari(""),2000);}}>✓</button>
                    </div>
                    {aileBasari&&<p className="ayarBasari">{aileBasari}</p>}
                  </>
                )}
              </div>
              {!yoneticiMi && (
                <div className="familyMemberSettingsNotice" role="note">
                  <div className="familyMemberSettingsNoticeGlow" aria-hidden="true" />
                  <img
                    className="familyMemberSettingsNoticeImage"
                    src={ailemAsset}
                    alt=""
                    aria-hidden="true"
                  />
                  <div className="familyMemberSettingsNoticeText">
                    <span className="familyMemberSettingsNoticeBadge" aria-hidden="true">◆</span>
                    <h3>{lang === "en" ? "Family settings are managed by admins" : "Aile ayarlarını yöneticiler düzenler"}</h3>
                    <p>
                      {lang === "en"
                        ? "Only family admins can change settings and manage members."
                        : "Ayarları değiştirme ve üyeleri yönetme işlemleri yalnızca aile yöneticileri tarafından yapılabilir."}
                    </p>
                  </div>
                </div>
              )}
              {yoneticiMi && (
                <>
                  <div className="ayarGrup">
                    <FamilyInvitePanel familyId={familyId} familyName={familyName} source="settings_family_tab" variant="settings" accentColor={profil?.calendarColor || "#6366f1"} />
                  </div>
                  <div className="ayarGrup">
                    <div className="ayarGrupBaslik">{t("settings.family.appGuideTitle")}</div>
                    <p className="ayarAciklama">{t("settings.family.appGuideHint")}</p>
                    <button type="button" className="ayarBtn" onClick={() => { kapat(); window.requestAnimationFrame(() => { window.setTimeout(() => onStartAppTour?.(), 220); }); }}>
                      🧭 {t("settings.family.startAppGuide")}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── RENKLER ── */}
          {bolum==="renk"&&(
            <div className="ayarGrup">
              <div className="ayarGrupBaslik">{t("settings.colors.title")}</div>
              <p className="ayarAciklama">{t("settings.colors.hint")}</p>
              {uyelerSirala(uyeler).map(u=>(
                <div key={u.uid} className="uyeRenkRow">
                  <div className="memberAvatar sm">{u.ad?.[0]}{u.soyad?.[0]}</div>
                  <span className="uyeRenkAd">{u.ad} {u.soyad}</span>
                  <div className="renkSecPalette">
                    {RENKLER.map(r=>(
                      <button key={r} className={`renkDot ${(u.calendarColor||"#2563eb")===r?"secili":""}`}
                        style={{background:r}} onClick={()=>uyeRenkGuncelle(u.uid,r)} title={r}/>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── GİZLİLİK ── */}
          {bolum==="gizlilik"&&(
            <>
              {/* Yasal belgeler — 3 kare kart yan yana */}
              <div className="ayarGrup">
                <div className="ayarGrupBaslik">{t("settings.privacy.legalDocuments")}</div>
                <div className="legalKartGrid">
                  <button className="legalKart" onClick={() => yasalSayfaAc("/privacy")}>
                    <span className="legalKartIkon">🔏</span>
                    <span className="legalKartEtiket">{t("settings.privacy.privacyPolicy")}</span>
                  </button>
                  <button className="legalKart" onClick={() => yasalSayfaAc("/terms")}>
                    <span className="legalKartIkon">📋</span>
                    <span className="legalKartEtiket">{t("settings.privacy.termsOfService")}</span>
                  </button>
                  <button className="legalKart" onClick={() => yasalSayfaAc("/account-deletion")}>
                    <span className="legalKartIkon">📄</span>
                    <span className="legalKartEtiket">{t("settings.privacy.accountDeletionInfo")}</span>
                  </button>
                </div>
              </div>
              <div className="ayarGrup">
                <div className="ayarGrupBaslik">{t("settings.privacy.loginMethods")}</div>
                <p className="ayarAciklama">{t("settings.privacy.loginMethodsHint")}</p>
                <div className="ayarToggleRow">
                  {[
                    // MADDE 3: Apple logosu daha once U+F8FF ozel karakteriyle
                    // gosteriliyordu; bu glyph yalnizca Apple cihazlarinin sistem
                    // fontunda bulundugu icin Android'de HIC gorunmuyordu.
                    // Giris ekraninda oldugu gibi burada da gercek SVG kullaniyoruz.
                    ["google.com", "G", t("auth.social.google")],
                    ["apple.com", (<svg viewBox="0 0 384 512" width="14" height="14" focusable="false" style={{ verticalAlign: "-2px" }}><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>), t("auth.social.apple")],
                  ].map(([provider, icon, label]) => {
                    const connected = authProviders.includes(provider);
                    const loading = sosyalBaglamaYukleniyor === provider;
                    return (
                      <button
                        key={provider}
                        type="button"
                        className={`ayarToggleBtn ${connected ? "active" : ""}`}
                        disabled={connected || Boolean(sosyalBaglamaYukleniyor)}
                        onClick={async () => {
                          try {
                            setSosyalBaglamaYukleniyor(provider);
                            const linked = await sosyalHesapBagla?.(provider);
                            if (linked) {
                              await appAlert(t("auth.social.linkSuccess"), { modal: true, title: t("settings.privacy.loginMethods") });
                            }
                          } catch (e) {
                            await appAlert(e?.message || t("auth.social.error.generic"), { modal: true, title: t("settings.privacy.loginMethods") });
                          } finally {
                            setSosyalBaglamaYukleniyor("");
                          }
                        }}
                      >
                        <span aria-hidden="true">{icon}</span>{" "}
                        {loading ? t("common.loading") : connected ? t("settings.privacy.connected") : `${label} · ${t("settings.privacy.connect")}`}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Veri & konum — yan yana 2 aksiyon kart */}
              <div className="ayarGrup">
                <div className="gizlilikAksiyonGrid">
                  <button
                    className="gizlilikAksiyonKart"
                    disabled={exportYukleniyor}
                    onClick={async()=>{
                      if (!verileriDisaAktar) return;
                      try {
                        setExportYukleniyor(true);
                        await verileriDisaAktar();
                      } catch (e) {
                        alert(e?.message || "Veriler dışa aktarılamadı.");
                      } finally {
                        setExportYukleniyor(false);
                      }
                    }}
                  >
                    <span className="gizlilikAksiyonIkon">📦</span>
                    <span className="gizlilikAksiyonBaslik">{t("settings.privacy.exportBtn")}</span>
                    <span className="gizlilikAksiyonAciklama">{t("settings.privacy.exportHint")}</span>
                  </button>
                  <button
                    className="gizlilikAksiyonKart"
                    disabled={konumGecmisiSilYukleniyor}
                    onClick={async () => {
                      const onay = await appConfirm(
                        t("settings.privacy.deleteLocationHistoryHint"),
                        {
                          title: t("settings.privacy.deleteLocationHistory"),
                          confirmText: t("settings.privacy.deleteLocationHistoryBtn"),
                          cancelText: t("common.cancel"),
                          danger: true,
                        }
                      );
                      if (!onay) return;
                      try {
                        setKonumGecmisiSilYukleniyor(true);
                        await konumGecmisiniSilService({ familyId: profil?.familyId });
                        await appAlert(t("settings.privacy.deleteLocationHistorySuccess"), {
                          modal: true,
                          title: t("settings.privacy.deleteLocationHistory"),
                        });
                      } catch (e) {
                        await appAlert(e?.message || (lang === "en" ? "Could not delete location history." : "Konum geçmişi silinemedi."), {
                          modal: true,
                          title: t("settings.privacy.deleteLocationHistory"),
                        });
                      } finally {
                        setKonumGecmisiSilYukleniyor(false);
                      }
                    }}
                  >
                    <span className="gizlilikAksiyonIkon">🗺️</span>
                    <span className="gizlilikAksiyonBaslik">{t("settings.privacy.deleteLocationHistoryBtn")}</span>
                    <span className="gizlilikAksiyonAciklama">{t("settings.privacy.deleteLocationHistoryHint")}</span>
                  </button>
                </div>
              </div>
              <div className="ayarGrup">
                <div className="ayarGrupBaslik ayarDanger">{t("settings.privacy.deleteAccount")}</div>
                <p className="ayarAciklama">{t("settings.privacy.deleteHint")}</p>
                {!silOnay?(
                  <button className="ayarDangerBtn" onClick={()=>setSilOnay(true)}>{t("settings.privacy.deleteBtn")}</button>
                ):(
                  <>
                    {usePasswordForDeletion ? (
                      <input className="darkInput" type="password" placeholder={t("settings.privacy.confirmPasswordPlaceholder")} value={silSifre} onChange={e=>setSilSifre(e.target.value)} style={{marginBottom:"8px"}}/>
                    ) : (
                      <p className="ayarAciklama">{t("settings.privacy.socialDeleteHint")}</p>
                    )}
                    <div className="formRow">
                      {usePasswordForDeletion ? (
                        <button className="ayarDangerBtn" disabled={silYukleniyor} onClick={() => hesabiKaliciSil({ password: silSifre })}>
                          {silYukleniyor ? (t("common.loading") || "Bekleyin...") : t("settings.privacy.confirmDelete")}
                        </button>
                      ) : deletionSocialProviders.map((provider) => (
                        <button key={provider} className="ayarDangerBtn" disabled={silYukleniyor} onClick={() => hesabiKaliciSil({ provider })}>
                          {silYukleniyor
                            ? (t("common.loading") || "Bekleyin...")
                            : (<>{provider === "apple.com" ? (<svg viewBox="0 0 384 512" width="14" height="14" focusable="false" style={{ verticalAlign: "-2px" }}><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" /></svg>) : "G"}{" "}{t("settings.privacy.socialReauth")}</>)}
                        </button>
                      ))}
                      <button className="ayarBtn" disabled={silYukleniyor} onClick={()=>{setSilOnay(false);setSilSifre("");}}>{t("common.cancel")}</button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* ── HAKKINDA ── */}
          {bolum==="hakkinda"&&(
            <div className="ayarGrup">
              <div className="hakkindaLogoWrap">
  <img
    src="/logo512.png"
    alt={t("app.name")}
    className="hakkindaLogo"
  />
</div>
              <div className="hakkindaAd">{t("app.name")}</div>
              <div className="hakkindaVersiyon">
                {runtimeVersion ? `${lang === "en" ? "Version" : "Versiyon"} ${runtimeVersion}` : t("settings.about.version")}
              </div>
              <p className="ayarAciklama" style={{textAlign:"center"}}>
                {t("settings.about.description").split("\n").map((line, i) => (
                  <React.Fragment key={i}>{line}{i === 0 && <br/>}</React.Fragment>
                ))}
              </p>
              <div className={`hakkindaAksiyonlar${Capacitor.isNativePlatform() ? " native" : ""}`}>
                {Capacitor.isNativePlatform() && (
                  <button
                    type="button"
                    className="hakkindaAksiyonBtn hakkindaDegerlendirBtn"
                    onClick={async () => {
                      const result = await openStoreReviewPage(settingsUid);
                      if (result?.status === "error") {
                        await appAlert(t("settings.about.rateError"), { modal: true, title: t("settings.about.rateApp") });
                      }
                    }}
                  >
                    <svg className="hakkindaAksiyonIkon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="m12 2.7 2.82 5.72 6.31.92-4.57 4.45 1.08 6.28L12 17.1l-5.64 2.97 1.08-6.28-4.57-4.45 6.31-.92L12 2.7Z" />
                    </svg>
                    <span>{t("settings.about.rateApp")}</span>
                  </button>
                )}
                <button type="button" className="hakkindaAksiyonBtn" onClick={() => { kapat?.(); onOpenFeedback?.(); }}>
                  <span className="hakkindaAksiyonEmoji" aria-hidden="true">✉️</span>
                  <span>{t("settings.about.feedbackShort")}</span>
                </button>
              </div>
              <div className="hakkindaSosyalBolum">
                <span className="hakkindaSosyalBaslik">{t("settings.about.followUs")}</span>
                <div className="hakkindaSosyalGrid">
                  <button
                    type="button"
                    className="hakkindaSosyalBtn instagram"
                    onClick={() => openAboutLink(ABOUT_LINKS.instagram).catch(() => appAlert(t("settings.about.linkError"), { modal: true }))}
                    aria-label={t("settings.about.instagramAria")}
                  >
                    <span className="hakkindaSosyalAura" aria-hidden="true" />
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <rect x="3" y="3" width="18" height="18" rx="5" />
                      <circle cx="12" cy="12" r="4.1" />
                      <circle cx="17.4" cy="6.7" r="1" className="solid" />
                    </svg>
                    <span><b>Instagram</b><small>@aileyonetimiapp</small></span>
                    <i aria-hidden="true">↗</i>
                  </button>
                  <button
                    type="button"
                    className="hakkindaSosyalBtn website"
                    onClick={() => openAboutLink(ABOUT_LINKS.website).catch(() => appAlert(t("settings.about.linkError"), { modal: true }))}
                    aria-label={t("settings.about.websiteAria")}
                  >
                    <span className="hakkindaSosyalAura" aria-hidden="true" />
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3.5 12h17M12 3c2.35 2.45 3.55 5.45 3.55 9S14.35 18.55 12 21M12 3C9.65 5.45 8.45 8.45 8.45 12S9.65 18.55 12 21" />
                    </svg>
                    <span><b>{t("settings.about.website")}</b><small>aileyonetimi.com</small></span>
                    <i aria-hidden="true">↗</i>
                  </button>
                </div>
              </div>
              <div className="hakkindaGelistirici">{t("settings.about.developer")}</div>
            </div>
          )}

          </div>{/* ayarlarIcerik kapanış */}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   ANA HOMEPAGE
───────────────────────────────────────── */
function HomePage({
  profil, setAktifSayfa, permissions, cikisYap,
  familyId, familyName, yoneticiMi,
  uyeler=[], setSeciliUye, ailedenAyril,
  toplamUrun=0, bekleyenUrun=0, alinanUrun=0,
  yaklasanlar=[], toplamEtkinlikBuAy=0, netBakiye=0, toplamGorev=0, toplamOrtakHarcama=0,
  toplamSaglikKaydi=0, toplamBelge=0, belgeler=[], cocukKartSayisi=0, cocukTakipKaydi=0, aktifKonumPaylasan=0, toplamHaritaKaydi=0,
  paraFormatla=(s)=>`${Number(s||0).toLocaleString("tr-TR")} ₺`,
  profilGuncelle, profilFotografiYukle, profilFotografiSil, sifreGuncelle,
  ayarlarAcik, setAyarlarAcik,
  tema, setTema, paraBirimi, setParaBirimi,
  bildirimAcik, setBildirimAcik, bildirimGun, setBildirimGun,
  varsayilanSayfa, setVarsayilanSayfa,
  alisverisHatirlatici, setAlisverisHatirlatici,
  hesapSil, aileAdiGuncelle, uyeRenkGuncelle, ownerUid,
  sosyalHesapBagla,
  okunmamisChat = 0,
  okunmamisChatOda = 0,
  toplamSohbet = 0,
  onBildirimAc,
  toplamOkunmamisBildirim = 0,
  user,
  onAileDegisti,
  isPremium = false,
  premiumConfig = {},
  onPremiumLimitHit,
  dilGuncelle,
  verileriDisaAktar,
  isSuperAdmin = false,
  onOpenLegal,
  onOpenFeedback,
  onStartAppTour,
}) {
  const { t, lang, tList } = useLanguage();
  const initials = `${profil?.ad?.[0]||""}${profil?.soyad?.[0]||""}`;
  const kullaniciRenk = profil?.calendarColor || "#6366f1";
  const familyStatusPreferenceUid = String(user?.firebase_uid || user?.uid || profil?.firebase_uid || profil?.uid || "").trim();
  const [profilAcik, setProfilAcik] = useState(false);
  const [familyModalAcik, setFamilyModalAcik] = useState(false);
  const [aktifWidget, setAktifWidget] = useState(0); // gerçek ilk widget index 0
  const aktifWidgetRef = useRef(0);
  const [aileSwitcherAcik, setAileSwitcherAcik] = useState(false);
  const [managementGuardOpen, setManagementGuardOpen] = useState(false);
  const [assistantSummary, setAssistantSummary] = useState(() => readCachedAssistantSummary(familyId, lang) || null);
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [familyStatusVisible, setFamilyStatusVisible] = useState(() => readFamilyStatusVisibility(familyStatusPreferenceUid, familyId));

  useEffect(() => {
    const openFamilyCard = () => setFamilyModalAcik(true);
    window.addEventListener("fp:openFamilyCard", openFamilyCard);
    return () => window.removeEventListener("fp:openFamilyCard", openFamilyCard);
  }, []);

  useEffect(() => {
    setFamilyStatusVisible(readFamilyStatusVisibility(familyStatusPreferenceUid, familyId));
  }, [familyStatusPreferenceUid, familyId]);

  const changeFamilyStatusVisibility = (visible) => {
    const next = visible !== false;
    setFamilyStatusVisible(next);
    try { localStorage.setItem(familyStatusVisibilityStorageKey(familyStatusPreferenceUid, familyId), String(next)); } catch {}
  };

  useBodyScrollLock(profilAcik || familyModalAcik || ayarlarAcik);



  const sliderRef = useRef(null);
  const [sliderReady, setSliderReady] = useState(false);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const pointerStartX = useRef(0);
  const pointerStartY = useRef(0);
  const pointerScrollStart = useRef(0);
  const mouseDragging = useRef(false);
  const autoScrollRef = useRef(null);
  const scrollSettleRef = useRef(null);
  const resizeFrameRef = useRef(null);

  /* ── Ana sayfa yüklendikten sonra ilk girişte push izni iste ── */
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!profil?.uid || !familyId) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "default") return;

    const izinKey = `familyPlannerPushPermissionAsked_${profil.uid}`;
    if (localStorage.getItem(izinKey) === "true") return;

    let iptal = false;

    const timer = setTimeout(async () => {
      if (iptal) return;

      try {
        await requestAndSaveNotificationToken({
          user: profil,
          familyId,
          silent: false,
        });
      } catch (error) {
        console.warn(
          "Ana sayfa sonrası bildirim izni alınamadı:",
          error?.message || error
        );
      } finally {
        localStorage.setItem(izinKey, "true");
      }
    }, 1200);

    return () => {
      iptal = true;
      clearTimeout(timer);
    };
  }, [profil?.uid, profil, familyId]);

  const allowed=(key)=>{
  if (key === "userManagement") return true;
  if (key === "assistant") return permissions?.pages?.assistant !== false;
  return permissions?.pages?.[key] !== false;
};
  useEffect(() => {
    if (!familyId) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      setAssistantLoading(true);
      getAssistantSummary(familyId, lang)
        .then((summary) => { if (!cancelled) setAssistantSummary(summary); })
        .catch(() => {
          if (!cancelled) {
            setAssistantSummary(buildLocalAssistantFallback({}, {
              lang, netBakiye, toplamOrtakHarcama, toplamSaglikKaydi, toplamBelge,
              cocukKartSayisi, cocukTakipKaydi, aktifKonumPaylasan, okunmamisChat, toplamOkunmamisBildirim
            }));
          }
        })
        .finally(() => { if (!cancelled) setAssistantLoading(false); });
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [
    familyId, lang, netBakiye, toplamUrun, bekleyenUrun, alinanUrun, yaklasanlar.length, toplamEtkinlikBuAy, toplamGorev,
    toplamOrtakHarcama, toplamSaglikKaydi, toplamBelge, cocukKartSayisi, cocukTakipKaydi,
    aktifKonumPaylasan, okunmamisChat, okunmamisChatOda, toplamOkunmamisBildirim
  ]);

  // Asistan kartı ve widget metinleri daima canlı frontend sayaçlarını esas alır.
  // Backend summary cache'inde 0 dönen eski değerler varsa ana sayfada 0 görünmesini engeller.
  const liveTaskCount = Number(toplamGorev || 0);
  const liveShoppingCount = Number(bekleyenUrun || 0);
  const liveUpcomingEventCount = Array.isArray(yaklasanlar) ? yaklasanlar.length : 0;
  const liveMonthEventCount = Number(toplamEtkinlikBuAy || 0);
  const liveKonuSayisi = liveTaskCount + liveShoppingCount + liveUpcomingEventCount;
  const liveShortText = lang === "en"
    ? `${liveTaskCount} task${liveTaskCount!==1?"s":""}, ${liveShoppingCount} shopping item${liveShoppingCount!==1?"s":""} and ${liveUpcomingEventCount} event${liveUpcomingEventCount!==1?"s":""} on the radar.`
    : `${liveTaskCount} görev, ${liveShoppingCount} alışveriş ürünü ve ${liveUpcomingEventCount} etkinlik takipte.`;

  const assistantModuleText = (moduleKey, fallback) => {
    if (moduleKey === "tasks") {
      return lang === "en" ? `${liveTaskCount} active task${liveTaskCount!==1?"s":""}.` : `${liveTaskCount} aktif görev var.`;
    }
    if (moduleKey === "shopping") {
      return lang === "en" ? `${liveShoppingCount} item${liveShoppingCount!==1?"s":""} waiting.` : `Alışverişte ${liveShoppingCount} ürün bekliyor.`;
    }
    if (moduleKey === "calendar") {
      return lang === "en"
        ? `${liveUpcomingEventCount} upcoming event${liveUpcomingEventCount!==1?"s":""}, ${liveMonthEventCount} this month.`
        : `${liveUpcomingEventCount} yaklaşan etkinlik, bu ay ${liveMonthEventCount} etkinlik var.`;
    }
    return assistantSummary?.modules?.[moduleKey]?.shortText || fallback;
  };

  const assistantWidgetText = assistantLoading && !assistantSummary
    ? (lang === "en" ? "Preparing your daily family summary." : "Aileniz için günlük özet hazırlanıyor.")
    : liveShortText;

  const assistantWidgetStatNum = liveKonuSayisi;


  const saglikWidgetStat = Number(toplamSaglikKaydi || 0);
  const belgeWidgetStat = Number(toplamBelge || 0);
  const cocukWidgetStat = Number(cocukKartSayisi || 0);
  const cocukWidgetLabel = lang === "en"
    ? `Child · ${Number(cocukTakipKaydi || 0)} tracking records`
    : `Çocuk · ${Number(cocukTakipKaydi || 0)} takip kaydı`;
  const haritaWidgetStat = Number(aktifKonumPaylasan || 0);
  const chatRoomCount = Number(toplamSohbet || 0);
  const onlineMemberCount = uyeler.filter((member) => member?.isOnline === true || member?.online === true).length;
  const documentExpiryCounts = belgeler.reduce((counts, document) => {
    if (!document?.gecerlilikTarihi) return counts;
    const days = Math.ceil((new Date(document.gecerlilikTarihi) - new Date()) / 86400000);
    if (!Number.isFinite(days)) return counts;
    if (days < 0) counts.expired += 1;
    else if (days <= 30) counts.approaching += 1;
    return counts;
  }, { expired: 0, approaching: 0 });
  const documentSummary = documentExpiryCounts.expired > 0
    ? (lang === "en" ? `${documentExpiryCounts.expired} document${documentExpiryCounts.expired !== 1 ? "s have" : " has"} expired` : `${documentExpiryCounts.expired} Belgenin süresi doldu`)
    : documentExpiryCounts.approaching > 0
      ? (lang === "en" ? `${documentExpiryCounts.approaching} document${documentExpiryCounts.approaching !== 1 ? "s are" : " is"} expiring soon` : `${documentExpiryCounts.approaching} Belgenin süresi yaklaşıyor`)
      : (lang === "en" ? `${belgeWidgetStat} document${belgeWidgetStat !== 1 ? "s look" : " looks"} up to date` : `${belgeWidgetStat} Belge güncel görünüyor`);
  const childWidgetText = cocukWidgetStat > 0
    ? (lang === "en" ? `${cocukWidgetStat} profile${cocukWidgetStat !== 1 ? "s" : ""} and ${Number(cocukTakipKaydi || 0)} tracking record${Number(cocukTakipKaydi || 0) !== 1 ? "s" : ""} are together here.` : `${cocukWidgetStat} profil ve ${Number(cocukTakipKaydi || 0)} takip kaydı burada birlikte büyüyor.`)
    : (lang === "en" ? "Keep growth, health and special memories together." : "Gelişim, sağlık ve özel anıları birlikte takip et.");
  const mapWidgetText = Number(toplamHaritaKaydi || 0) > 0
    ? (lang === "en" ? `${Number(toplamHaritaKaydi)} saved map record${Number(toplamHaritaKaydi) !== 1 ? "s" : ""} are ready to explore.` : `Haritada ${Number(toplamHaritaKaydi)} kayıt birlikte görüntüleniyor.`)
    : (lang === "en" ? "Save meaningful places and see your family map at a glance." : "Önemli yerleri kaydet, aile haritanı tek bakışta gör.");
  const taskWidgetText = liveTaskCount > 0
    ? (lang === "en" ? `${liveTaskCount} active task${liveTaskCount !== 1 ? "s are" : " is"} waiting for the next step.` : `${liveTaskCount} aktif görev sıradaki adımı bekliyor.`)
    : (lang === "en" ? "Add a task and organize the next step together." : "Yeni bir görev ekle, sıradaki adımı birlikte planla.");
  const chatWidgetText = okunmamisChat > 0
    ? assistantModuleText("chat", lang === "en" ? "New family messages are waiting for you." : "Ailenden yeni mesajlar seni bekliyor.")
    : chatRoomCount > 0
      ? (lang === "en" ? `There ${chatRoomCount === 1 ? "is" : "are"} ${chatRoomCount} chat room${chatRoomCount !== 1 ? "s" : ""} in Family Chat.` : `Aile sohbetinde ${chatRoomCount} sohbet odası var.`)
      : (lang === "en" ? "Start the first family conversation." : "Ailenle ilk sohbeti başlat.");

  // Tüm olası widgetler - izne göre filtrele
  // Sıralama: Aile Asistanı, Takvim, Finans, Alışveriş, Aile Sohbeti, Sağlık Verileri,
  // Harcamalar, Çocuk Takibi, Aile Haritası, Görevler, Belgeler, Bugün Ne Pişirsem.
  const tumWidgetler = [
    {key:"assistant",permission:"assistant",className:"heroCard assistantHero",page:"assistant",badge:lang === "en" ? "FAMILY ASSISTANT" : "AİLE ASİSTANI",title:t("nav.assistant"),text:assistantWidgetText,statNum:assistantWidgetStatNum,statLabel:lang === "en" ? "Topic" : "Konu",asset:aileAsistaniAsset, emoji:""},
    {key:"takvim",permission:"calendar",className:"heroCard blueHero",page:"takvim",badge:lang === "en" ? "📅 CALENDAR" : "📅 TAKVİM",title:t("nav.calendar"),text:assistantModuleText("calendar", lang === "en" ? "Track all family events in one place" : "Tüm aile etkinliklerini tek yerden takip et"),statNum:yaklasanlar.length,statLabel:lang === "en" ? "Upcoming Event" : "Yaklaşan Etkinlik",asset:takvimAsset, emoji:"📅"},
    {key:"finans",permission:"finance",className:"heroCard greenHero",page:"finans",badge:lang === "en" ? "💰 FINANCE" : "💰 FİNANS",title:t("nav.finance"),text:assistantModuleText("finance", lang === "en" ? "Live summary of income and expenses" : "Gelir ve giderlerin anlık özeti"),statNum:paraFormatla(netBakiye),statLabel:lang === "en" ? "Net Balance" : "Net Bakiye",asset:finansAsset, emoji:"💰"},
    {key:"alisveris",permission:"shopping",className:"heroCard amberHero",page:"alisveris",badge:lang === "en" ? "🛒 SHOPPING" : "🛒 ALIŞVERİŞ",title:t("nav.shopping"),text:assistantModuleText("shopping", lang === "en" ? "Add missing items instantly and track them" : "Eksikleri anında ekle ve takip et"),statNum:bekleyenUrun,statLabel:lang === "en" ? "Pending Item" : "Bekleyen Ürün",asset:alisverisAsset, emoji:"🛒"},
    {key:"chat",permission:"chat",className:"heroCard indigoHero",page:"chat",badge:lang === "en" ? "💬 FAMILY CHAT" : "💬 AİLE SOHBETİ",title:t("nav.chat"),text:chatWidgetText,
      statNum: okunmamisChat > 0
        ? (okunmamisChatOda > 1 ? `${okunmamisChatOda} ${lang === "en" ? "chats" : "sohbet"}` : `${okunmamisChat}`)
        : chatRoomCount,
      statLabel: okunmamisChat > 0
        ? (okunmamisChatOda > 1 ? `${okunmamisChat} ${t("home.newMessages")}` : t("home.unreadMessages"))
        : (lang === "en" ? (chatRoomCount === 1 ? "Chat Room" : "Chat Rooms") : "Sohbet Odası"),
      asset:aileSohbetiAsset, emoji:"💬"},
    {key:"saglik",permission:"health",className:"heroCard pinkHero",page:"saglik",badge:lang === "en" ? "❤️ HEALTH DATA" : "❤️ SAĞLIK VERİLERİ",title:t("nav.health"),
      text: saglikWidgetStat > 0
        ? (lang === "en" ? `${saglikWidgetStat} health record${saglikWidgetStat !== 1 ? "s" : ""} tracked` : `${saglikWidgetStat} sağlık kaydı takip ediliyor`)
        : assistantModuleText("health", lang === "en" ? "Track family health cards and reminders" : "Aile sağlık kartlarını ve hatırlatmaları takip et"),
      statNum:saglikWidgetStat,statLabel:lang === "en" ? "Health Record" : "Sağlık Kaydı",asset:saglikAsset, emoji:"❤️"},
    {key:"harcama",permission:"sharedExpenses",className:"heroCard roseHero",page:"harcama",badge:lang === "en" ? "💸 EXPENSES" : "💸 HARCAMALAR",title:t("nav.sharedExpenses"),text:assistantModuleText("sharedExpenses", lang === "en" ? "Manage family expenses together" : "Aile masraflarını birlikte yönetin"),statNum:paraFormatla(toplamOrtakHarcama),statLabel:lang === "en" ? "This Month Total" : "Bu Ay Toplam",asset:harcamalarAsset, emoji:"💸"},
    {key:"cocuk",permission:"children",className:"heroCard violetHero",page:"cocuk",badge:lang === "en" ? "👶 CHILD TRACKING" : "👶 ÇOCUK TAKİBİ",title:t("nav.children"),text:childWidgetText,statNum:cocukWidgetStat,statLabel:cocukWidgetLabel,asset:cocukTakibiAsset, emoji:"👶"},
    {key:"harita",permission:"harita",className:"heroCard tealHero",page:"harita",badge:lang === "en" ? "🗺️ FAMILY MAP" : "🗺️ AİLE HARİTASI",title:t("nav.map"),text:mapWidgetText,statNum:haritaWidgetStat,statLabel:lang === "en" ? "People Sharing Location" : "Kişi Konum Paylaşıyor",asset:aileHaritasiAsset, emoji:"🗺️"},
    {key:"gorev",permission:"tasks",className:"heroCard purpleHero",page:"gorev",badge:lang === "en" ? "✅ TASKS" : "✅ GÖREVLER",title:t("nav.tasks"),text:taskWidgetText,statNum:toplamGorev,statLabel:lang === "en" ? "Active Task" : "Aktif Görev",asset:gorevlerAsset, emoji:"✅"},
    {key:"belgeler",permission:"documents",className:"heroCard slateHero",page:"belgeler",badge:lang === "en" ? "📁 DOCUMENTS" : "📁 BELGELER",title:t("nav.documents"),text:documentSummary,statNum:belgeWidgetStat,statLabel:lang === "en" ? "Document" : "Belge",asset:belgelerAsset, emoji:"📁"},
    {key:"nePisirsam",permission:"nePisirsam",className:"heroCard orangeHero",page:"nePisirsam",badge:lang === "en" ? "🍽️ WHAT SHOULD I COOK?" : "🍽️ BUGÜN NE PİŞİRSEM",title:t("nav.cooking"),text:lang === "en" ? "Get menu ideas for family tables" : "Aile sofralarına uygun menü önerileri alın",statNum:"500",statLabel:lang === "en" ? "Meal Ideas" : "Yemek Önerisi",asset:nePisirsemAsset, emoji:"🍽️"},
  ];
  const widgetler = tumWidgetler.filter(w => allowed(w.permission) && !isAndroidRestrictedPage(w.page));
  const W = widgetler.length;
  const cloneOffset = W > 1 ? 1 : 0;
  const sonsuz = W > 1 ? [widgetler[W - 1], ...widgetler, widgetler[0]] : widgetler;
  const widgetKeySignature = widgetler.map(widget => widget.key).join("|");
  const getW = () => sliderRef.current?.clientWidth || 0;

  const clearAutoScroll = () => {
    if (autoScrollRef.current) {
      clearTimeout(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  const clearScrollSettle = () => {
    if (scrollSettleRef.current) {
      clearTimeout(scrollSettleRef.current);
      scrollSettleRef.current = null;
    }
  };

  const gercekIndex = (rawIndex) => {
    if (W <= 1) return 0;
    return ((rawIndex - cloneOffset) % W + W) % W;
  };

  const aktifWidgetGuncelle = (rawIndex) => {
    if (!W) return;
    const yeniIndex = gercekIndex(rawIndex);
    if (aktifWidgetRef.current === yeniIndex) return;
    aktifWidgetRef.current = yeniIndex;
    setAktifWidget(yeniIndex);
  };

  const startAutoScroll = () => {
    clearAutoScroll();
    if (W <= 1 || (typeof document !== "undefined" && document.hidden)) return;

    const currentWidget = widgetler[aktifWidgetRef.current] || widgetler[0];
    const delay = currentWidget?.key === "assistant" ? 10000 : 5000;

    autoScrollRef.current = setTimeout(() => {
      if (isDragging.current) {
        startAutoScroll();
        return;
      }
      const slider = sliderRef.current;
      const width = getW();
      if (!slider || !width) return;

      const currentRealIndex = Math.max(0, Math.min(W - 1, aktifWidgetRef.current));
      const currentRawIndex = currentRealIndex + cloneOffset;
      slider.scrollTo({ left: (currentRawIndex + 1) * width, behavior: "smooth" });
    }, delay);
  };

  const carouselKonumunuSabitle = () => {
    const slider = sliderRef.current;
    const width = getW();
    if (!slider || !width || !W) return;

    const rawIndex = Math.max(0, Math.min(sonsuz.length - 1, Math.round(slider.scrollLeft / width)));
    const hedefSol = rawIndex * width;

    // Her cihazda bırakılan kartı tam genişliğe oturt.
    if (Math.abs(slider.scrollLeft - hedefSol) > 1) {
      slider.scrollTo({ left: hedefSol, behavior: "smooth" });
      return;
    }

    aktifWidgetGuncelle(rawIndex);

    // Uçlardaki kopya karttan, aynı görünen gerçek karta animasyonsuz geç.
    // Kullanıcı bu geçişi görmez ve aynı yönde kaydırmaya devam edebilir.
    if (W > 1 && (rawIndex === 0 || rawIndex === W + 1)) {
      const gercekRawIndex = rawIndex === 0 ? W : 1;
      const oncekiScrollBehavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = "auto";
      slider.scrollLeft = gercekRawIndex * width;
      requestAnimationFrame(() => {
        if (sliderRef.current === slider) slider.style.scrollBehavior = oncekiScrollBehavior;
      });
    }

    startAutoScroll();
  };

  const onScroll = () => {
    const slider = sliderRef.current;
    const width = getW();
    if (!slider || !width || !W) return;

    clearAutoScroll();
    if (isDragging.current || Math.abs(slider.scrollLeft - pointerScrollStart.current) > 4) hasMoved.current = true;
    aktifWidgetGuncelle(Math.round(slider.scrollLeft / width));
    clearScrollSettle();
    scrollSettleRef.current = setTimeout(carouselKonumunuSabitle, 140);
  };

  const onPointerDown = (event) => {
    const slider = sliderRef.current;
    if (!slider) return;
    clearAutoScroll();
    clearScrollSettle();
    isDragging.current = true;
    hasMoved.current = false;
    pointerStartX.current = event.clientX;
    pointerStartY.current = event.clientY;
    pointerScrollStart.current = slider.scrollLeft;
    mouseDragging.current = event.pointerType === "mouse";
    slider.classList.add("is-interacting");
    if (mouseDragging.current) {
      try { slider.setPointerCapture(event.pointerId); } catch (_) {}
    }
  };

  const onPointerMove = (event) => {
    if (!isDragging.current) return;
    const slider = sliderRef.current;
    if (!slider) return;
    const deltaX = event.clientX - pointerStartX.current;
    const deltaY = event.clientY - pointerStartY.current;
    if (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6) hasMoved.current = true;

    // Telefonda native momentum kullanılır; masaüstünde tutup sürükleme korunur.
    if (mouseDragging.current) {
      event.preventDefault();
      slider.scrollLeft = pointerScrollStart.current - deltaX;
    }
  };

  const onPointerUp = (event) => {
    const slider = sliderRef.current;
    if (!slider || !isDragging.current) return;
    const masaustuSurukleme = mouseDragging.current;
    isDragging.current = false;
    mouseDragging.current = false;
    slider.classList.remove("is-interacting");
    if (masaustuSurukleme) {
      try { slider.releasePointerCapture(event.pointerId); } catch (_) {}
      const width = getW();
      if (width) slider.scrollTo({ left: Math.round(slider.scrollLeft / width) * width, behavior: "smooth" });
    }
    clearScrollSettle();
    scrollSettleRef.current = setTimeout(carouselKonumunuSabitle, 160);
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    setSliderReady(false);
    aktifWidgetRef.current = 0;
    setAktifWidget(0);

    const hizala = () => {
      const width = slider.clientWidth;
      if (!width || !W) return;
      const rawIndex = Math.max(0, Math.min(W - 1, aktifWidgetRef.current)) + cloneOffset;
      slider.scrollLeft = rawIndex * width;
      setSliderReady(true);
      startAutoScroll();
    };

    resizeFrameRef.current = requestAnimationFrame(hizala);
    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
          if (resizeFrameRef.current) cancelAnimationFrame(resizeFrameRef.current);
          resizeFrameRef.current = requestAnimationFrame(hizala);
        })
      : null;
    resizeObserver?.observe(slider);
    window.addEventListener("resize", hizala);
    slider.addEventListener("scrollend", carouselKonumunuSabitle);

    return () => {
      clearAutoScroll();
      clearScrollSettle();
      if (resizeFrameRef.current) cancelAnimationFrame(resizeFrameRef.current);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", hizala);
      slider.removeEventListener("scrollend", carouselKonumunuSabitle);
    };
  // Widget içeriği değişse bile sıra değişmedikçe carousel yeniden kurulmaz.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetKeySignature]);

  useEffect(() => {
    const visibilityDegisti = () => {
      if (document.hidden) clearAutoScroll();
      else startAutoScroll();
    };
    document.addEventListener("visibilitychange", visibilityDegisti);
    return () => document.removeEventListener("visibilitychange", visibilityDegisti);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetKeySignature]);

  const menuCards=[
    {title:t("nav.shopping"),
      sub: toplamUrun > 0
        ? (lang === "en" ? `${bekleyenUrun} pending, ${toplamUrun} total` : `${bekleyenUrun} bekliyor, ${toplamUrun} ürün`)
        : (lang === "en" ? "Add items to your shopping list" : "Alışveriş listene ürün ekle"),
      asset:alisverisAsset,page:"alisveris",permission:"shopping",color:"yellow"},
    {title:t("nav.calendar"),
      sub: yaklasanlar.length
        ? `${yaklasanlar.length} ${t("calendar.upcoming")}`
        : t("home.managePlans"),
      asset:takvimAsset,page:"takvim",permission:"calendar",color:"purple"},
    {title:t("nav.finance"),
      sub: lang === "en"
        ? `Net: ${paraFormatla(netBakiye)}`
        : `Net bakiye: ${paraFormatla(netBakiye)}`,
      asset:finansAsset,page:"finans",permission:"finance",color:"green"},
    {title:t("nav.tasks"),
      sub: toplamGorev > 0
        ? (lang === "en" ? `${toplamGorev} active task${toplamGorev > 1 ? "s" : ""}` : `${toplamGorev} aktif görev`)
        : (lang === "en" ? "Add your first task" : "İlk görevini ekle"),
      asset:gorevlerAsset,page:"gorev",permission:"tasks",color:"blue"},
    {title:t("nav.sharedExpenses"),
      sub: toplamOrtakHarcama > 0
        ? (lang === "en" ? `Total: ${paraFormatla(toplamOrtakHarcama)}` : `Toplam: ${paraFormatla(toplamOrtakHarcama)}`)
        : (lang === "en" ? "Track shared expenses together" : "Ortak masrafları birlikte takip et"),
      asset:harcamalarAsset,page:"harcama",permission:"sharedExpenses",color:"orange"},
    {title:t("nav.health"),
      sub: saglikWidgetStat > 0
        ? (lang === "en" ? `${saglikWidgetStat} health record${saglikWidgetStat !== 1 ? "s" : ""}` : `${saglikWidgetStat} sağlık kaydı`)
        : (lang === "en" ? "Track family health records" : "Aile sağlık kayıtlarını takip et"),
      asset:saglikAsset,page:"saglik",permission:"health",color:"pink"},
    {title:t("nav.map"),
      sub: haritaWidgetStat > 0
        ? (lang === "en" ? `${haritaWidgetStat} member${haritaWidgetStat > 1 ? "s" : ""} sharing location` : `${haritaWidgetStat} kişi konum paylaşıyor`)
        : (lang === "en" ? "See family locations on one screen" : "Aile konumlarını tek ekranda gör"),
      asset:aileHaritasiAsset,page:"harita",permission:"harita",color:"teal"},
    {title:t("nav.children"),
      sub: cocukWidgetStat > 0
        ? (lang === "en" ? `${cocukWidgetStat} child${cocukWidgetStat > 1 ? "ren" : ""}, ${Number(cocukTakipKaydi||0)} records` : `${cocukWidgetStat} çocuk · ${Number(cocukTakipKaydi||0)} takip kaydı`)
        : (lang === "en" ? "Track growth and development" : "Büyüme ve gelişimi takip et"),
      asset:cocukTakibiAsset,page:"cocuk",permission:"children",color:"purple"},
    {title:t("nav.chat"),
      sub: okunmamisChat > 0
        ? (okunmamisChatOda > 1 ? `${okunmamisChatOda} ${lang === "en" ? "chats" : "sohbetten"}\n${okunmamisChat} ${lang === "en" ? "new messages" : "yeni mesaj"}` : `${okunmamisChat} ${lang === "en" ? "new message" : "yeni mesaj"}`)
        : chatRoomCount > 0
          ? (lang === "en" ? `${chatRoomCount} chat room${chatRoomCount !== 1 ? "s" : ""}` : `${chatRoomCount} sohbet odası`)
          : (lang === "en" ? "Chat with your family" : "Aileyle sohbet et"),
      asset:aileSohbetiAsset,page:"chat",permission:"chat",color:"indigo"},
    {title:t("nav.documents"),
      sub: documentSummary,
      asset:belgelerAsset,page:"belgeler",permission:"documents",color:"teal"},
    {title:t("nav.cooking"),sub:t("home.cookingSub"),asset:nePisirsemAsset,page:"nePisirsam",permission:"nePisirsam",color:"orange"},
    {title:t("home.family"),sub:`${uyeler.length} ${t("home.familyMembers")}${onlineMemberCount > 0 ? `\n${onlineMemberCount} ${lang === "en" ? (onlineMemberCount === 1 ? "member active" : "members active") : "üye aktif"}` : ""}`,asset:ailemAsset,action:"family",permission:"home",color:"mint"},
    {title:t("nav.activity"),sub:t("home.activitySub"),icon:"✨",page:"activity",permission:"activity",color:"indigo"},
    {title:t("nav.management"),sub:t("home.managementSub"),asset:yonetimAsset,page:"kullaniciYonetimi",permission:"userManagement",color:"pink"},
    // Aile Asistanı — her zaman en sonda, izne tabi
    {title:t("nav.assistant"),sub:assistantWidgetText,asset:aileAsistaniAsset,page:"assistant",permission:"assistant",color:"ai",isAssistant:true},
  ];

  // Görünür kart listesi + tek/çift tespiti
  const visibleMenuCards = menuCards.filter(c => allowed(c.permission) && !isAndroidRestrictedPage(c.page));
  const isOddMenuCount   = visibleMenuCards.length % 2 === 1;

  return (
    <div className="modernHome" style={{ "--primary-color": kullaniciRenk }}>
      <div className="homeTopBar">
        <AvatarImg
          avatar={profil?.avatar || null}
          uid={user?.firebase_uid || user?.uid || user?.id || profil?.firebase_uid || profil?.uid}
          fileKey={profil?.profilePictureFileKey}
          renk={kullaniciRenk}
          initials={initials || "FP"}
          size={48}
          glow={true}
          radius="50%"
          className="homeAvatar"
          data-tour-id="profile"
          style={{ cursor: "pointer" }}
          onClick={() => setProfilAcik(true)}
        />

        <button
          className="aileSwitcherTopBtn"
          data-tour-id="family-switcher"
          onClick={()=>setAileSwitcherAcik(true)}
          aria-label={lang === "en" ? "Switch family" : "Aile değiştir"}
        >
          <span className="aileSwitcherTopBtnIkon">🏠</span>
          <span className="aileSwitcherTopBtnAd">{familyName}</span>
          <svg className="aileSwitcherTopBtnChevron" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="homeTopActions">
          <button className="homeBildirimBtn" data-tour-id="notifications" onClick={onBildirimAc} aria-label={lang === "en" ? "Notifications" : "Bildirimler"}>
            🔔
            {toplamOkunmamisBildirim > 0 && (
              <span className="homeBildirimBadge">{toplamOkunmamisBildirim > 99 ? "99+" : toplamOkunmamisBildirim}</span>
            )}
          </button>
          <button className="homeSettings" data-tour-id="settings" onClick={()=>setAyarlarAcik(true)}>⚙️</button>
        </div>
      </div>

      {aileSwitcherAcik && (
        <AileSwitcher
          user={user}
          profil={profil}
          familyId={familyId}
          familyName={familyName}
          yoneticiMi={yoneticiMi}
          onAileDegisti={onAileDegisti}
          onKapat={()=>setAileSwitcherAcik(false)}
          isPremium={isPremium}
          premiumConfig={premiumConfig}
          onPremiumLimitHit={onPremiumLimitHit}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      <div ref={sliderRef} className="heroSlider" data-tour-id="hero" onScroll={onScroll}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        style={{
  touchAction: "pan-x pan-y pinch-zoom",
  userSelect: "none",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorX: "contain",
  opacity: sliderReady ? 1 : 0,
  transition: sliderReady ? "opacity 0.18s ease" : "none",
}}>
        {sonsuz.map((w,i)=>(
          <div key={`${w.key}-${i}`} className={w.className} onClick={()=>{if(!hasMoved.current)setAktifSayfa(w.page);}}>
            <div className="heroOrb heroOrb1"/>
            <div className="heroOrb heroOrb2"/>
            <div className="heroShine"/>
            <div className="heroCardInner">
              <div className="heroCardBadge heroCardTitleBadge">{w.badge}</div>
              <p>{w.text}</p>
              <div className="heroCardStat">
                <span className="heroStatNum">{w.statNum}</span>
                <span className="heroStatLabel">{w.statLabel}</span>
              </div>
            </div>
            <div className="heroEmoji">
              {w.asset ? <img src={w.asset} alt="" aria-hidden="true" className="heroAssetIcon" /> : w.emoji}
            </div>
          </div>
        ))}
      </div>
      <div className="heroDots">{widgetler.map((_,i)=><span key={i} className={aktifWidget===i?"heroDot active":"heroDot"}/>)}</div>

      <div className="homeMiniStats" data-tour-id="quick-stats">
        <div
  className="shoppingMiniStat clickable"
  onClick={() => setAktifSayfa("alisveris")}
>
  <strong>{toplamUrun}</strong>
  <span>{t("shopping.product")}</span>
</div>

<div
  className="shoppingMiniStat clickable shoppingMiniStatBekleyen"
  onClick={() => setAktifSayfa("alisveris")}
>
  <strong>{bekleyenUrun}</strong>
  <span>{lang === "en" ? "Pending" : "Bekleyen"}</span>
</div>

<div
  className="shoppingMiniStat clickable shoppingMiniStatAlinan"
  onClick={() => setAktifSayfa("alisveris")}
>
  <strong>{alinanUrun}</strong>
  <span>{t("shopping.bought")}</span>
</div>
      </div>

      {familyStatusVisible && (
        <FamilyStatusPreview profil={profil} user={user} familyId={familyId} uyeler={uyeler} lang={lang} accentColor={kullaniciRenk} />
      )}

            <div className="modernMenuGrid" data-tour-id="modules">
        {visibleMenuCards.map((c, idx)=>{
          const isLastCard  = idx === visibleMenuCards.length - 1;
          const isFullWidth = isLastCard && isOddMenuCount;
          const isManagementCard = c.page === "kullaniciYonetimi";
          const showManagementGuard = isManagementCard && managementGuardOpen && !yoneticiMi;

          const handleMenuCardClick = () => {
            if (c.action === "family") {
              setManagementGuardOpen(false);
              setFamilyModalAcik(true);
              return;
            }

            if (isManagementCard && !yoneticiMi) {
              setManagementGuardOpen(true);
              window.clearTimeout(window.__familyPlannerManagementGuardTimer);
              window.__familyPlannerManagementGuardTimer = window.setTimeout(() => {
                setManagementGuardOpen(false);
              }, 3200);
              return;
            }

            setManagementGuardOpen(false);
            if (c.page) setAktifSayfa(c.page);
          };

          return (
            <div key={c.title} className={`modernMenuCardWrap${isFullWidth ? " lastCardFull" : ""}`}>
              <button
                className={isManagementCard && !yoneticiMi ? "modernMenuCard managementCardLocked" : "modernMenuCard"}
                onClick={handleMenuCardClick}
                aria-describedby={showManagementGuard ? "management-card-guard" : undefined}
              >
                <div className="modernMenuCardText"><h3>{c.title}</h3>
                  <p className="modernMenuCardSub">{c.sub && c.sub.includes('\n')
                    ? c.sub.split('\n').map((s,i)=><span key={i} style={{display:"block"}}>{s}</span>)
                    : c.sub}
                  </p>
                </div>
                <span className={`modernIcon ${c.color}`}>
                  {c.asset ? <img src={c.asset} alt="" aria-hidden="true" className="modernAssetIcon" /> : c.icon}
                </span>
              </button>

              {showManagementGuard && (
                <div id="management-card-guard" className="managementCardGuardBubble" role="status">
                  <span className="managementCardGuardIcon">🔐</span>
                  <span>{lang === "en" ? "Only family admins can access this section." : "Bu bölüme yalnızca aile yöneticileri erişebilir."}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Ailem Modal */}
      {familyModalAcik&&(
        <div className="modalOverlay" onClick={()=>setFamilyModalAcik(false)}>
          <div className="darkModal familyMembersModal" onClick={e=>e.stopPropagation()}>
            <div className="darkModalHeader">
              <span className="darkModalTitle"><img src="/logo512.png" alt={t("app.name")} style={{width:24,height:24,borderRadius:8,objectFit:"cover",verticalAlign:"middle",marginRight:8}} /> {t("home.family")}</span>
              <button className="darkModalClose" onClick={()=>setFamilyModalAcik(false)}>×</button>
            </div>
            <p className="darkModalSub">{t("family.tapMemberDetails")}</p>
            {yoneticiMi && (
              <FamilyInvitePanel
                familyId={familyId}
                familyName={familyName}
                source="home_family_card"
                variant="family-modal"
                accentColor={kullaniciRenk}
              />
            )}
            <div className="darkMembersList">
              {uyelerSirala(uyeler).map(u=>{
                const memberInitials = `${u.ad?.[0] || ""}${u.soyad?.[0] || ""}` || (u.displayName?.[0] || "?");
                const memberColor = u.calendarColor || u.color || u.renk || "#6366f1";
                const memberName = `${u.ad || ""} ${u.soyad || ""}`.trim() || u.displayName || u.email || t("family.member");
                const profileRole = normalizeFamilyProfileRole(u.rol || u.profilRol || "");
                const familyRole = u.aileYetkisi || u.role || t("family.member");
                return (
                  <button key={u.id || u.uid} className="darkMemberRow" onClick={()=>setSeciliUye(u)}>
                    <AvatarImg
                      avatar={u.avatar || null}
                      renk={memberColor}
                      initials={memberInitials}
                      size={42}
                      glow={false}
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                    <div className="memberInfo">
                      <span className="familyMemberName">{memberName}</span>
                      <small className="familyMemberRole">{profileRole ? `${translateKnownListValue(tList, profileRole) || profileRole} - ` : ""}{translateKnownListValue(tList, familyRole) || familyRole}</small>
                    </div>
                    <span className={`memberPresenceMini${(u.isOnline || u.online) ? " online" : ""}`}>
                      {formatMemberPresenceText(u, lang, user?.uid || profil?.uid)}
                    </span>
                    <span className="memberChevron">›</span>
                  </button>
                );
              })}
            </div>
            <button className="darkDangerBtn" onClick={ailedenAyril}>{t("family.leaveGroup")}</button>
            <button className="darkSecondaryBtn" onClick={()=>setFamilyModalAcik(false)}>{t("common.close")}</button>
          </div>
        </div>
      )}

      {profilAcik&&<ProfilPanel profil={profil} cikisYap={cikisYap} profilGuncelle={profilGuncelle} profilFotografiYukle={profilFotografiYukle} profilFotografiSil={profilFotografiSil} sifreGuncelle={sifreGuncelle} kapat={()=>setProfilAcik(false)}/>}

      {ayarlarAcik&&(
        <AyarlarPanel
          profil={profil} tema={tema} setTema={setTema}
          paraBirimi={paraBirimi} setParaBirimi={setParaBirimi}
          bildirimAcik={bildirimAcik} setBildirimAcik={setBildirimAcik}
          bildirimGun={bildirimGun} setBildirimGun={setBildirimGun}
          varsayilanSayfa={varsayilanSayfa} setVarsayilanSayfa={setVarsayilanSayfa}
          alisverisHatirlatici={alisverisHatirlatici} setAlisverisHatirlatici={setAlisverisHatirlatici}
          familyId={familyId} familyName={familyName}
          yoneticiMi={yoneticiMi} aileAdiGuncelle={aileAdiGuncelle}
          uyeler={uyeler} uyeRenkGuncelle={uyeRenkGuncelle}
          familyStatusVisible={familyStatusVisible}
          onFamilyStatusVisibleChange={changeFamilyStatusVisibility}
          hesapSil={hesapSil} kapat={()=>setAyarlarAcik(false)}
          sosyalHesapBagla={sosyalHesapBagla}
          dilGuncelle={dilGuncelle}
          verileriDisaAktar={verileriDisaAktar}
          onOpenLegal={onOpenLegal}
          onOpenFeedback={onOpenFeedback}
          onStartAppTour={onStartAppTour}
        />
      )}
    </div>
  );
}

export default HomePage;
